import { useState, useEffect, useRef } from "react";

/* ─── RESPONSIVE HOOKS ─── */
function useIsMobile() {
  const [v, setV] = useState(() => window.innerWidth < 768);
  useEffect(() => {
    const h = () => setV(window.innerWidth < 768);
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, []);
  return v;
}
function useIsTablet() {
  const [v, setV] = useState(() => window.innerWidth < 1024);
  useEffect(() => {
    const h = () => setV(window.innerWidth < 1024);
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, []);
  return v;
}

/* ─── PEEPAL TREE LOGO ─── */
function PeepalLogo({ size = 40, showText = true, onClick }) {
  return showText ? (
    <img src="/peepal-logo-full.png" alt="Peepal Research — Innovation, Insight, Integrity" style={{ height: size, width: "auto", display: "block", cursor: "pointer" }} onClick={onClick}/>
  ) : (
    <img src="/peepal-logo-icon.png" alt="Peepal Research" style={{ height: size, width: "auto", display: "block", cursor: "pointer" }} onClick={onClick}/>
  );
}

/* ─── DATA ─── */
const NAV_LINKS = ["Home","About","Solutions","Careers","Insights","Contact"];

const SOLUTIONS = [
  { name:"NetOrchestra AI", tagline:"AI-Native Network Management", description:"The self-healing brain for multi-vendor networks. NetOrchestra AI autonomously detects, diagnoses, and remediates network faults — reducing MTTR from hours to under 5 minutes across any vendor, any protocol.", emoji:"🌐", color:"#047857",
    features:[
      "Architected to provide multi-vendor support",
      "Works across all vendor equipment",
      "Standards-based — OpenConfig & TMF specifications",
      "Coexists with vendor-provided NMS software",
      "Real-time telemetry streaming",
      "AI root-cause correlation",
      "Continuous polling loop as a safety net",
      "RAG-augmented runbook knowledge",
      "Optional AI Remediation module for autonomous healing",
      "Flexible alarm resolution based on your organization's policies"
    ] },
];

// Stats removed — not displayed on homepage

const BLOGS = [
  {
    id: "blog-4",
    title: "AccelerateOTT 2026: Top Takeaways and Startup World Cup Ottawa Winners",
    excerpt: "Peepal Research competed among Ottawa's top ten startups at the AccelerateOTT 2026 Startup World Cup Regional, as Kavodax took first place and a shot at the $1M USD Global Grand Finale in San Francisco.",
    date: "Jun 2026",
    readTime: "4 min read",
    category: "Press",
    img: "https://images.unsplash.com/photo-1550751827-4bd374c3f58b?auto=format&fit=crop&w=800&q=80",
    externalUrl: "https://www.investottawa.ca/blog/accelerateott-2026-top-takeaways-and-startup-world-cup-ottawa-winners/",
    content: [
      { type:"p", text:"AccelerateOTT 2026 brought founders, investors, and ecosystem leaders together at Lansdowne Park for a full day exploring what it takes to build companies in the age of AI — capped off by the Startup World Cup Ottawa Regional Competition." },
      { type:"p", text:"Peepal Research was one of ten Eastern Ontario finalists who took the stage, pitching alongside companies building everything from FDA-designated medical devices to university spinouts. Kavodax, a stablecoin-powered B2B payments platform, took first place and a $25,000 grant, advancing to the Global Grand Finale in San Francisco later this year." },
      { type:"p", text:"Read the full recap, including the top takeaways on building with AI and the complete list of winners, on Invest Ottawa's website." },
    ]
  },
  {
    id: "blog-5",
    title: "Ottawa's Founders Take a Global Stage at Startup World Cup",
    excerpt: "UpNext covers the June 2026 Startup World Cup Ottawa Regional, where Peepal Research joined nine other Eastern Ontario startups pitching under the theme \"Built for the Age of AI.\"",
    date: "Jun 2026",
    readTime: "3 min read",
    category: "Press",
    img: "https://images.unsplash.com/photo-1519389950473-47ba0277781c?auto=format&fit=crop&w=800&q=80",
    externalUrl: "https://upnext.world/accelerateott-2026-ottawa-take-a-global-stage/",
    content: [
      { type:"p", text:"On June 11, 2026, ten Eastern Ontario startups took the stage at Lansdowne Park for the Startup World Cup Ottawa Regional, competing under the theme \"Built for the Age of AI.\" Peepal Research, building self-healing intelligence systems for complex multi-vendor networks, was among the cohort." },
      { type:"p", text:"Kavodax, Canada's first stablecoin-powered B2B payment platform for SMEs, claimed the top spot and a place at the Global Grand Finale in San Francisco this November, with the top three finalists sharing $50,000 in non-dilutive grant funding from FedDev Ontario." },
      { type:"p", text:"Read the full feature on UpNext to meet the rest of the founders and see what set this year's cohort apart." },
    ]
  },
  {
    id: "blog-6",
    title: "Meet the Founders: Peepal Research Named a Startup World Cup Ottawa Finalist",
    excerpt: "Invest Ottawa's innovateON profiles the ten founders competing at the 2026 Startup World Cup Ottawa Regional, including Peepal Research's work on self-healing intelligence systems for complex networks.",
    date: "May 2026",
    readTime: "5 min read",
    category: "Press",
    img: "https://images.unsplash.com/photo-1556761175-5973dc0f32e7?auto=format&fit=crop&w=800&q=80",
    externalUrl: "https://innovateon.ca/ten-startups-one-global-platform-introducing-the-founders-competing-at-the-startup-world-cup-ottawa-regional-competition/",
    content: [
      { type:"p", text:"Ahead of the Startup World Cup Ottawa Regional Competition, Invest Ottawa's innovateON profiled the ten finalists set to pitch at Lansdowne Park on June 11, 2026 — including Peepal Research, described as building \"self-healing intelligence systems for complex networks.\"" },
      { type:"p", text:"The cohort spanned life sciences, fintech, AI, and infrastructure, with companies like Kavodax (cross-border payments infrastructure) and Taskd (deterministic AI for enterprise decision-making) joining Peepal Research on the shortlist for $50,000 in non-dilutive funding and a shot at the $1M USD Global Grand Finale in San Francisco." },
      { type:"p", text:"Read the full founder profiles on innovateON." },
    ]
  },
  {
    id: "blog-7",
    title: "AccelerateOTT 2026: Event Details and Agenda",
    excerpt: "Official event information for AccelerateOTT 2026 at Ottawa's Horticulture Building — home to the Startup World Cup Ottawa Regional, where Peepal Research pitched alongside nine other finalists.",
    date: "Jun 2026",
    readTime: "2 min read",
    category: "Press",
    img: "https://images.unsplash.com/photo-1560439514-4e9645039924?auto=format&fit=crop&w=800&q=80",
    externalUrl: "https://www.accelerateott.ca/event-information/",
    content: [
      { type:"p", text:"AccelerateOTT brought together founders, investors, and ecosystem builders for a day of keynotes, founder stories, and live pitches at Ottawa's Horticulture Building — capped by the Startup World Cup Ottawa Regional Competition, where Peepal Research took the stage as one of ten finalists." },
      { type:"p", text:"The official event page covers full logistics — agenda, venue access, and the Accelerate After Hours networking reception — for anyone tracking future editions." },
      { type:"p", text:"See the full event details on AccelerateOTT's website." },
    ]
  },
  {
    id: "blog-1",
    title: "Introducing NetOrchestra AI: The Self-Healing Network Platform",
    excerpt: "How Peepal Research built a 7-stage autonomous remediation pipeline that closes 76% of multi-vendor network faults without human intervention.",
    date: "Jan 2026",
    readTime: "7 min read",
    category: "Product",
    img: "https://images.unsplash.com/photo-1558494949-ef010cbdcc31?auto=format&fit=crop&w=800&q=80",
    externalUrl: null,
    content: [
      { type:"h2", text:"The NOC Problem We Set Out to Solve" },
      { type:"p", text:"When we started building NetOrchestra AI, we kept hearing the same thing from NOC teams: 65% of their tasks were still manual, MTTR on complex multi-vendor faults averaged 6–8 hours, and every new vendor meant another NMS silo. Operators were drowning in alerts — not managing networks." },
      { type:"h2", text:"The 10-Stage Remediation Pipeline" },
      { type:"p", text:"NetOrchestra AI's core is a structured autonomous pipeline: Correlate → Govern → Plan → Blast Radius → Execute → Verify → Resolve. Each stage is deterministic and auditable. The result: 76% of faults auto-closed, 7x MTTR reduction vs. incumbent NMS platforms." },
      { type:"h2", text:"Multi-Vendor from Day One" },
      { type:"p", text:"Our LLM-powered Adapter Agent AI generates NETCONF/gNMI/REST adapters for Nokia SR Linux, Arista EOS, Juniper cRPD, Cisco IOS XR, and Ciena WaveServer — without manual YANG coding. Add a new vendor and the platform adapts." },
    ]
  },
  {
    id: "blog-2",
    title: "Why Carriers Are Stuck Running Multiple NMS Platforms — and What to Do About It",
    excerpt: "Open RAN mandates created a multi-vendor fragmentation crisis. We explain why single-pane-of-glass NMS is now both possible and commercially urgent.",
    date: "Dec 2025",
    readTime: "6 min read",
    category: "Industry",
    img: "https://images.unsplash.com/photo-1535223289827-42f1e9919769?auto=format&fit=crop&w=800&q=80",
    externalUrl: null,
    content: [
      { type:"h2", text:"The Multi-Vendor Fragmentation Problem" },
      { type:"p", text:"Most carriers now run multiple NMS platforms from different vendors — separate teams, separate opex, zero correlation. This wasn't by choice. Open RAN mandates forced telcos off single-vendor networks, and incumbent NMS vendors never solved cross-domain visibility." },
      { type:"h2", text:"Three Tailwinds Making This Solvable Today" },
      { type:"p", text:"GPT-4-class models first reached production-quality network reasoning in 2024. Telco runbooks, vendor MIBs, and 3GPP specs can now be embedded and queried in real time. Combined with NIS2/FCC regulatory pressure and the 5G multi-vendor crisis, the conditions for autonomous NMS have finally arrived." },
      { type:"h2", text:"The NetOrchestra AI Approach" },
      { type:"p", text:"NetOrchestra AI deploys alongside what you run today — no rip-and-replace. It covers the gaps your incumbent NMS leaves open, and adds the autonomous remediation layer that no legacy platform provides. Time to value: days, not 6–12 months." },
    ]
  },
  {
    id: "blog-3",
    title: "Peepal Research Selected for Startup World Cup Ottawa 2026",
    excerpt: "Peepal Research has been selected as one of ten Ottawa-based startups to compete at the Startup World Cup Ottawa Regional Competition.",
    date: "Oct 2025",
    readTime: "3 min read",
    category: "Press",
    img: "https://images.unsplash.com/photo-1504711434969-e33886168f5c?auto=format&fit=crop&w=800&q=80",
    externalUrl: "https://www.investottawa.ca/blog/ten-startups-one-global-stage-meet-the-founders-competing-at-startup-world-cup-ottawa-regional-competition/",
    content: [
      { type:"p", text:"Peepal Research has been selected as one of ten Ottawa-based startups to compete at the Startup World Cup Ottawa Regional Competition, hosted by Invest Ottawa." },
      { type:"p", text:"The Startup World Cup is one of the world's largest startup competitions, connecting promising early-stage companies with global investors and industry leaders." },
      { type:"p", text:"Read the full feature on Invest Ottawa's website to meet all ten competing founders and learn more about the competition." },
    ]
  },
];

const FAQS = [
  { q:"What is NetOrchestra AI?",
    a:"NetOrchestra AI is an AI-native network management system that autonomously detects, diagnoses, and remediates faults across multi-vendor networks. It reduces MTTR from 6–8 hours to under 5 minutes and closes 76% of alarms without human intervention." },
  { q:"Which vendors does NetOrchestra AI support?",
    a:"NetOrchestra AI supports Nokia SR Linux, Arista EOS, Juniper cRPD, Cisco IOS XR, and Ciena WaveServer out of the box. Our LLM-powered Adapter Agent AI can generate new vendor adapters via NETCONF/gNMI/REST — without manual YANG coding." },
  { q:"Do I need to replace my existing NMS?",
    a:"No. NetOrchestra AI deploys alongside your existing NMS — no rip-and-replace. It covers the gaps your incumbent tools leave open and adds the autonomous remediation layer. Your existing vendor tools keep doing their job while we add the intelligence layer." },
  { q:"How long does deployment take?",
    a:"Time to value is days, not months. We deploy on your infrastructure (on-premises or cloud), connect to your existing device telemetry via gNMI/NETCONF, and have the first autonomous remediation running within the first week. Compared to 6–12 months for Nokia NSP or Cisco Crosswork." },
  { q:"What does it cost?",
    a:"NetOrchestra AI is offered as a simple per-node subscription — no multi-year lock-in, and no drawn-out custom-quote process. It's priced well below the enterprise licences incumbent platforms typically charge. Contact us at info@peepalresearch.com for a quote tailored to your network." },
  { q:"Is NetOrchestra AI compliant with telecom standards?",
    a:"Yes. NetOrchestra AI is TMF638, TMF641, and TMF642 compliant — the same standards Tier-1 telcos mandate for vendor onboarding. It also delivers the automated monitoring and incident-response documentation required by NIS2 (Europe) and FCC critical-infrastructure rules." },
];

/* ─── AI CHATBOT ─── */
const KB = [
  { keys:["what is netorchestra","what is peepal","what does it do","product overview"], answer: FAQS[0].a },
  { keys:["vendor","nokia","arista","juniper","cisco","ciena","multi-vendor"], answer: FAQS[1].a },
  { keys:["replace","existing nms","rip and replace","current system","incumbent"], answer: FAQS[2].a },
  { keys:["deploy","how long","time to value","implementation","onboarding"], answer: FAQS[3].a },
  { keys:["cost","price","pricing","how much","per node"], answer: FAQS[4].a },
  { keys:["compliant","compliance","standard","tmf","nis2","fcc","regulation"], answer: FAQS[5].a },
  { keys:["hire","talent","careers","job","recruit","staffing"], answer: "Yes. We offer pre-screened telecom talent services for operators and MSPs hiring OSS/NMS engineers, 5G architects, and NOC consultants. Visit our Careers page or contact us at careers@peepalresearch.com." },
];

function getLocalResponse(input) {
  const l = input.toLowerCase();
  for (const e of KB) if (e.keys.some(k => l.includes(k))) return e.answer;
  return null; // null = not found locally
}

async function callClaudeAPI(userMessage, useWebSearch) {
  const tools = useWebSearch ? [{ type: "web_search_20250305", name: "web_search" }] : [];
  const systemPrompt = `You are Peepal AI, the expert assistant for Peepal Research Inc. — an AI-native telecom software company based in Ottawa, Canada.

Peepal Research builds NetOrchestra AI: an AI-native NMS that autonomously detects, diagnoses, and remediates faults across multi-vendor networks (Nokia SR Linux, Arista EOS, Juniper cRPD, Cisco IOS XR, Ciena WaveServer). Key facts: 76% of faults auto-closed, 7x MTTR reduction, transparent per-node subscription pricing with no lock-in, TMF638/641/642 compliant, deploys in days not months.

Be concise, helpful, and use markdown **bold** for key terms. Keep answers under 120 words unless asked for detail. For demos/pricing: direct to info@peepalresearch.com.`;
  
  const body = {
    model: "claude-sonnet-4-20250514",
    max_tokens: 400,
    system: systemPrompt,
    messages: [{ role: "user", content: userMessage }],
    ...(tools.length > 0 && { tools }),
  };

  const res = await fetch("https://api.anthropic.com/v1/messages", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const data = await res.json();
  if (data.error) return `Sorry, I couldn't connect right now. Reach us at **info@peepalresearch.com** for help!`;
  const text = (data.content || []).filter(b => b.type === "text").map(b => b.text).join("\n").trim();
  return text || "I wasn't able to get a response. Please try again or contact **info@peepalresearch.com**.";
}

/* ─── UTILITIES ─── */
function useReveal() {
  const ref = useRef(null);
  const [v, setV] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setV(true); }, { threshold: 0.06 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return [ref, v];
}
function Reveal({ children, delay = 0 }) {
  const [ref, v] = useReveal();
  return (
    <div ref={ref} style={{ opacity: v ? 1 : 0, transform: v ? "none" : "translateY(22px)", transition: `opacity 0.6s ease ${delay}ms, transform 0.6s ease ${delay}ms` }}>
      {children}
    </div>
  );
}

function NetworkBg() {
  return (
    <div style={{ position:"absolute", inset:0, overflow:"hidden", zIndex:0, pointerEvents:"none" }}>
      <svg width="100%" height="100%" style={{ position:"absolute", inset:0, opacity:0.05 }}>
        <defs><pattern id="grid" width="56" height="56" patternUnits="userSpaceOnUse"><path d="M 56 0 L 0 0 0 56" fill="none" stroke="#faa12d" strokeWidth="0.6"/></pattern></defs>
        <rect width="100%" height="100%" fill="url(#grid)"/>
      </svg>
      <div style={{ position:"absolute", width:500, height:500, borderRadius:"50%", background:"radial-gradient(circle, rgba(250,161,45,0.09) 0%, transparent 70%)", top:"-120px", left:"-120px" }}/>
    </div>
  );
}

function Tag({ children, color = "#96611b", size, noCaps }) {
  return (
    <span style={{ display:"inline-block", padding: size ? "6px 18px" : "4px 14px", borderRadius:100, fontSize: size || "0.7rem", fontWeight:700, letterSpacing: noCaps ? "0.02em" : "0.09em", textTransform: noCaps ? "none" : "uppercase", background:`${color}15`, color, border:`1px solid ${color}30` }}>
      {children}
    </span>
  );
}

function ChatMd({ text }) {
  return <span>{text.split(/(\*\*[^*]+\*\*)/g).map((p,i) => p.startsWith("**") ? <strong key={i}>{p.slice(2,-2)}</strong> : p)}</span>;
}

function FaqItem({ faq, i }) {
  const [open, setOpen] = useState(false);
  return (
    <Reveal delay={i * 60}>
      <div style={{ border:`1px solid ${open ? "rgba(250,161,45,0.22)" : "rgba(15,23,42,0.09)"}`, borderRadius:12, marginBottom:"0.9rem", overflow:"hidden", background: open ? "rgba(250,161,45,0.04)" : "rgba(15,23,42,0.033)", transition:"all 0.3s" }}>
        <button style={{ width:"100%", textAlign:"left", background:"none", border:"none", color:"#0f172a", fontFamily:"inherit", cursor:"pointer", padding:"1.2rem 1.4rem", fontSize:".92rem", fontWeight:600, display:"flex", justifyContent:"space-between", alignItems:"center", gap:"1rem" }} onClick={() => setOpen(o => !o)}>
          <span style={{ lineHeight:1.4 }}>{faq.q}</span>
          <span style={{ color:"#96611b", fontSize:"1.4rem", fontWeight:300, transform: open ? "rotate(45deg)" : "none", transition:"transform 0.3s", flexShrink:0 }}>+</span>
        </button>
        {open && <div style={{ padding:"0 1.4rem 1.2rem", color:"#475569", lineHeight:1.75, borderTop:"1px solid rgba(15,23,42,0.067)", paddingTop:"1rem", fontSize:".88rem" }}>{faq.a}</div>}
      </div>
    </Reveal>
  );
}

function CtaBanner({ setActivePage, heading="Ready to modernize your network?", sub="Talk to our team and discover how Peepal Research can accelerate your digital transformation.", label="Request a Demo" }) {
  return (
    <section style={{ padding:"5rem 0", background:"linear-gradient(135deg,rgba(250,161,45,0.08),rgba(249,115,22,0.05))", borderTop:"1px solid rgba(250,161,45,0.12)", borderBottom:"1px solid rgba(250,161,45,0.12)" }}>
      <div style={{ maxWidth:1300, margin:"0 auto", padding:"0 1.25rem", textAlign:"center" }}>
        <Reveal>
          <h2 style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:"clamp(1.6rem,4vw,2.8rem)", fontWeight:800, color:"#0f172a", marginBottom:"1.1rem" }}>{heading}</h2>
          <p style={{ color:"#475569", fontSize:".975rem", maxWidth:500, margin:"0 auto 2.25rem", lineHeight:1.75 }}>{sub}</p>
          <button className="btn-p" style={{ fontSize:".975rem", padding:"14px 38px" }} onClick={() => setActivePage("Contact")}>{label} →</button>
        </Reveal>
      </div>
    </section>
  );
}

/* ═══════════════ MAIN APP ═══════════════ */
export default function App() {
  const [page, setPage]     = useState("Home");
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [solMenuOpen, setSolMenuOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [msgs, setMsgs]     = useState([{ from:"ai", text:"Hi! 👋 I'm **Peepal AI** — powered by Claude. Ask me about our OSS/NMS products, Telecom Talent, or anything telecom-related!\n\nToggle **🌐 Search Web** to get live internet answers." }]);
  const [chatIn, setChatIn] = useState("");
  const [typing, setTyping] = useState(false);
  const [webSearch, setWebSearch] = useState(false);
  const chatEnd = useRef(null);
  const [form, setForm]     = useState({ name:"", email:"", company:"", subject:"", message:"" });
  const [formSt, setFormSt] = useState("idle");
  const [nlEmail, setNlEmail] = useState("");
  const [nlSt, setNlSt]     = useState("idle");
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();

  useEffect(() => {
    const h = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", h); return () => window.removeEventListener("scroll", h);
  }, []);

  useEffect(() => { chatEnd.current?.scrollIntoView({ behavior:"smooth" }); }, [msgs, typing]);
  useEffect(() => { document.body.style.overflow = menuOpen ? "hidden" : ""; return () => { document.body.style.overflow = ""; }; }, [menuOpen]);

  const go = (p) => { setPage(p); setMenuOpen(false); window.scrollTo(0,0); };

  const sendChat = async () => {
    const t = chatIn.trim(); if (!t) return;
    setMsgs(m => [...m, { from:"user", text:t }]); setChatIn(""); setTyping(true);
    // Try local KB first (fast, no API call needed for product questions)
    const localAnswer = getLocalResponse(t);
    if (localAnswer && !webSearch) {
      setTimeout(() => { setTyping(false); setMsgs(m => [...m, { from:"ai", text:localAnswer }]); }, 600);
    } else {
      // Use Claude API
      try {
        const reply = await callClaudeAPI(t, webSearch);
        setTyping(false);
        setMsgs(m => [...m, { from:"ai", text:reply }]);
      } catch {
        setTyping(false);
        setMsgs(m => [...m, { from:"ai", text:"Sorry, I couldn't connect. Reach us at **info@peepalresearch.com**!" }]);
      }
    }
  };

  const submitContact = async (e) => {
    e.preventDefault(); setFormSt("sending");
    try {
      const fd = new FormData(); Object.entries(form).forEach(([k,v]) => fd.append(k,v));
      const res = await fetch("send_email.php", { method:"POST", body:fd });
      const html = await res.text();
      setFormSt(html.toLowerCase().includes("error") || html.toLowerCase().includes("incomplete") ? "error" : "sent");
    } catch { setFormSt("error"); }
  };

  const submitNewsletter = async () => {
    const email = nlEmail.trim();
    if (!email) return;
    setNlSt("sending");
    try {
      const fd = new FormData();
      fd.append("name", "Newsletter Subscriber");
      fd.append("email", email);
      fd.append("company", "");
      fd.append("subject", "Newsletter Subscription");
      fd.append("message", `New newsletter signup request from ${email}.`);
      const res = await fetch("send_email.php", { method:"POST", body:fd });
      const html = await res.text();
      if (html.toLowerCase().includes("error") || html.toLowerCase().includes("incomplete")) {
        setNlSt("error");
      } else {
        setNlSt("sent");
        setNlEmail("");
      }
    } catch { setNlSt("error"); }
  };

  const P = { isMobile, isTablet, setActivePage: go };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,400;0,9..40,500;0,9..40,700;1,9..40,400&family=Syne:wght@700;800;900&display=swap');
        *{margin:0;padding:0;box-sizing:border-box;}
        html{scroll-behavior:smooth;}
        body{background:#ffffff;font-family:'DM Sans',sans-serif;color:#0f172a;-webkit-font-smoothing:antialiased;overflow-x:hidden;}
        img{display:block;max-width:100%;}
        ::-webkit-scrollbar{width:4px;}
        ::-webkit-scrollbar-track{background:#f1f5f9;}
        ::-webkit-scrollbar-thumb{background:linear-gradient(#faa12d,#f97316);border-radius:2px;}
        @keyframes floatY{0%,100%{transform:translateY(0)}50%{transform:translateY(-14px)}}
        @keyframes pulse{0%,100%{opacity:.7;transform:scale(1)}50%{opacity:1;transform:scale(1.1)}}
        @keyframes blink{0%,80%,100%{opacity:0}40%{opacity:1}}
        @keyframes fadeIn{from{opacity:0}to{opacity:1}}
        @keyframes slideDown{from{opacity:0;transform:translateY(-8px)}to{opacity:1;transform:translateY(0)}}
        .btn-p{background:linear-gradient(135deg,#faa12d,#f97316);color:#fff;border:none;padding:13px 28px;border-radius:7px;font-size:.925rem;font-weight:600;cursor:pointer;transition:all .25s;display:inline-flex;align-items:center;justify-content:center;gap:8px;font-family:inherit;touch-action:manipulation;-webkit-tap-highlight-color:transparent;}
        .btn-p:hover{transform:translateY(-2px);box-shadow:0 12px 30px rgba(250,161,45,.35);}
        .btn-p:active{transform:translateY(0);opacity:.9;}
        .btn-o{background:transparent;color:#0f172a;border:1px solid rgba(15,23,42,0.263);padding:13px 28px;border-radius:7px;font-size:.925rem;font-weight:500;cursor:pointer;transition:all .25s;display:inline-flex;align-items:center;justify-content:center;gap:8px;font-family:inherit;touch-action:manipulation;-webkit-tap-highlight-color:transparent;}
        .btn-o:hover{background:rgba(15,23,42,0.079);border-color:rgba(15,23,42,0.47);}
        .btn-blue{background:linear-gradient(135deg,#0284c7,#0369a1) !important;}
        .btn-blue:hover{box-shadow:0 12px 30px rgba(3,105,161,.35) !important;}
        input,textarea{width:100%;background:rgba(15,23,42,0.056);border:1px solid rgba(15,23,42,0.125);border-radius:8px;color:#0f172a;padding:13px 16px;font-size:16px;font-family:inherit;transition:border .2s;outline:none;-webkit-appearance:none;}
        input:focus,textarea:focus{border-color:rgba(250,161,45,.55);background:rgba(250,161,45,.03);}
        input::placeholder,textarea::placeholder{color:#64748b;}
        textarea{resize:vertical;min-height:120px;}
        label{color:#334155;font-size:.82rem;display:block;margin-bottom:7px;font-weight:500;}
        .dot-typing span{display:inline-block;width:6px;height:6px;border-radius:50%;background:#faa12d;margin:0 2px;animation:blink 1.4s infinite both;}
        .dot-typing span:nth-child(2){animation-delay:.2s;}
        .dot-typing span:nth-child(3){animation-delay:.4s;}
        /* Mobile nav overlay */
        .m-nav{position:fixed;inset:0;background:rgba(255,255,255,0.98);z-index:999;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:1.6rem;animation:fadeIn .2s ease;}
        .m-nav-link{font-family:'Plus Jakarta Sans',sans-serif;font-size:1.5rem;font-weight:800;color:#0f172a;background:none;border:none;cursor:pointer;padding:.4rem 1rem;transition:color .2s;-webkit-tap-highlight-color:transparent;}
        .m-nav-link.active{color:#96611b;}
      `}</style>

      <div style={{ background:"#ffffff", minHeight:"100vh", overflowX:"hidden" }}>

        {/* ── HEADER ── */}
        <header style={{ position:"fixed", top:0, left:0, width:"100%", zIndex:1000, background: (scrolled || menuOpen) ? "rgba(255,255,255,0.9)" : "transparent", backdropFilter: scrolled ? "blur(18px)" : "none", borderBottom: scrolled ? "1px solid rgba(15,23,42,0.079)" : "none", transition:"background .35s, border .35s" }}>
          <div style={{ maxWidth:1300, margin:"0 auto", padding:"0 1.25rem", display:"flex", alignItems:"center", justifyContent:"space-between", height: isMobile ? 64 : 76 }}>
            <PeepalLogo size={isMobile ? 34 : 42} onClick={() => go("Home")} />

            {/* Desktop nav */}
            {!isMobile && (
              <nav style={{ display:"flex", gap:"1.6rem", alignItems:"center" }}>
                {NAV_LINKS.map(l => (
                  l === "Solutions" ? (
                    <div key={l} style={{ position:"relative" }} onMouseEnter={() => setSolMenuOpen(true)} onMouseLeave={() => setSolMenuOpen(false)}>
                      <button onClick={() => go(l)}
                        style={{ fontSize:".875rem", fontWeight:500, color: page===l ? "#0f172a" : "#334155", cursor:"pointer", padding:"6px 0", position:"relative", background:"none", border:"none", fontFamily:"inherit", transition:"color .2s", display:"flex", alignItems:"center", gap:4 }}
                        onMouseEnter={e => e.currentTarget.style.color="#0f172a"}
                        onMouseLeave={e => e.currentTarget.style.color = page===l ? "#0f172a" : "#334155"}>
                        {l}
                        <span style={{ fontSize:".6rem", transform: solMenuOpen ? "rotate(180deg)" : "none", transition:"transform .2s" }}>▾</span>
                        {page===l && <span style={{ position:"absolute", bottom:0, left:0, width:"100%", height:2, background:"#faa12d", borderRadius:2 }}/>}
                      </button>
                      {solMenuOpen && (
                        <div style={{ position:"absolute", top:"100%", left:0, paddingTop:8, zIndex:1200 }}>
                          <div style={{ background:"#ffffff", border:"1px solid rgba(15,23,42,0.1)", borderRadius:10, boxShadow:"0 18px 50px rgba(15,23,42,0.14)", padding:".4rem", minWidth:190, animation:"slideDown .18s ease" }}>
                            <button onClick={() => { go("Solutions"); setSolMenuOpen(false); }}
                              style={{ display:"flex", alignItems:"center", gap:8, width:"100%", textAlign:"left", padding:"9px 10px", borderRadius:7, background:"none", border:"none", cursor:"pointer", fontFamily:"inherit", fontSize:".85rem", color:"#334155", fontWeight:500 }}
                              onMouseEnter={e => e.currentTarget.style.background="rgba(15,23,42,0.05)"}
                              onMouseLeave={e => e.currentTarget.style.background="none"}>
                              🌐 NetOrchestra AI
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <button key={l} onClick={() => go(l)}
                      style={{ fontSize:".875rem", fontWeight:500, color: page===l ? "#0f172a" : "#334155", cursor:"pointer", padding:"6px 0", position:"relative", background:"none", border:"none", fontFamily:"inherit", transition:"color .2s" }}
                      onMouseEnter={e => e.currentTarget.style.color="#0f172a"}
                      onMouseLeave={e => e.currentTarget.style.color = page===l ? "#0f172a" : "#334155"}>
                      {l}
                      {page===l && <span style={{ position:"absolute", bottom:0, left:0, width:"100%", height:2, background:"#faa12d", borderRadius:2 }}/>}
                    </button>
                  )
                ))}
                <button className="btn-p" style={{ padding:"9px 20px", fontSize:".82rem" }} onClick={() => go("Contact")}>Request a Demo →</button>
              </nav>
            )}

            {/* Mobile hamburger */}
            {isMobile && (
              <button onClick={() => setMenuOpen(o => !o)} style={{ background:"none", border:"none", cursor:"pointer", padding:"8px", zIndex:1001, display:"flex", flexDirection:"column", gap:"5px" }} aria-label="Menu">
                <span style={{ display:"block", width:24, height:2, background:"#0f172a", borderRadius:2, transition:"all .3s", transform: menuOpen ? "rotate(45deg) translateY(7px)" : "none" }}/>
                <span style={{ display:"block", width:24, height:2, background:"#0f172a", borderRadius:2, transition:"all .3s", opacity: menuOpen ? 0 : 1 }}/>
                <span style={{ display:"block", width:24, height:2, background:"#0f172a", borderRadius:2, transition:"all .3s", transform: menuOpen ? "rotate(-45deg) translateY(-7px)" : "none" }}/>
              </button>
            )}
          </div>
        </header>

        {/* Mobile Full-screen Menu */}
        {isMobile && menuOpen && (
          <div className="m-nav">
            {NAV_LINKS.map(l => (
              <div key={l} style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:".5rem" }}>
                <button className={`m-nav-link ${page===l ? "active" : ""}`} onClick={() => go(l)}>{l}</button>
                {l === "Solutions" && (
                  <button style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:".95rem", fontWeight:600, color:"#64748b", background:"none", border:"none", cursor:"pointer", padding:".2rem 1rem" }} onClick={() => go("Solutions")}>
                    ↳ 🌐 NetOrchestra AI
                  </button>
                )}
              </div>
            ))}
            <button className="btn-p" style={{ marginTop:"1rem", padding:"14px 36px" }} onClick={() => go("Contact")}>Request a Demo →</button>
          </div>
        )}

        {/* ── MAIN ── */}
        <main style={{ paddingTop: isMobile ? 64 : 76 }}>
          {page==="Home"      && <HomePage      {...P} />}
          {page==="About"     && <AboutPage     {...P} />}
          {page==="Solutions" && <SolutionsPage {...P} />}
          {page==="Careers"    && <CareersPage    {...P} />}
          {page==="Insights"  && <InsightsPage  {...P} />}
          {page==="Contact"   && <ContactPage   {...P} form={form} setForm={setForm} formSt={formSt} submitContact={submitContact} />}
        </main>

        {/* ── FOOTER ── */}
        <footer style={{ background:"#ffffff", borderTop:"1px solid rgba(15,23,42,0.079)" }}>
          {/* Newsletter strip */}
          <div style={{ background:"rgba(250,161,45,0.06)", borderBottom:"1px solid rgba(250,161,45,0.1)", padding: isMobile ? "2.5rem 0" : "3rem 0" }}>
            <div style={{ maxWidth:1300, margin:"0 auto", padding:"0 1.25rem", display:"flex", flexDirection: isMobile ? "column" : "row", alignItems: isMobile ? "flex-start" : "center", justifyContent:"space-between", gap:"1.5rem" }}>
              <div>
                <h3 style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontWeight:800, fontSize:"1.2rem", color:"#0f172a", marginBottom:".35rem" }}>Subscribe to Our Newsletter</h3>
                <p style={{ color:"#475569", fontSize:".88rem" }}>Stay updated on AI-native OSS/NMS innovations and telecom industry insights.</p>
              </div>
              <div style={{ display:"flex", flexDirection:"column", gap:".4rem", flexShrink:0, width: isMobile ? "100%" : "auto" }}>
                {nlSt === "sent" ? (
                  <div style={{ display:"flex", alignItems:"center", gap:8, color:"#047857", fontSize:".9rem", fontWeight:600 }}>✅ Thanks for subscribing!</div>
                ) : (
                  <form onSubmit={e => { e.preventDefault(); submitNewsletter(); }} style={{ display:"flex", gap:".6rem", width: isMobile ? "100%" : "auto" }}>
                    <input type="email" required placeholder="Enter your email" value={nlEmail} onChange={e => setNlEmail(e.target.value)}
                      style={{ padding:"10px 16px", borderRadius:8, border:"1px solid rgba(15,23,42,0.125)", background:"rgba(15,23,42,0.067)", color:"#0f172a", fontSize:".88rem", fontFamily:"inherit", outline:"none", flex: isMobile ? 1 : "none", width: isMobile ? "auto" : 240 }}/>
                    <button className="btn-p" type="submit" disabled={nlSt==="sending"} style={{ padding:"10px 20px", fontSize:".85rem", flexShrink:0, opacity: nlSt==="sending" ? 0.7 : 1 }}>{nlSt==="sending" ? "Subscribing…" : "Subscribe"}</button>
                  </form>
                )}
                {nlSt === "error" && <div style={{ color:"#b91c1c", fontSize:".78rem" }}>Something went wrong — please try again.</div>}
              </div>
            </div>
          </div>

          {/* Main footer */}
          <div style={{ maxWidth:1300, margin:"0 auto", padding:"3.5rem 1.25rem 2rem" }}>
            <div style={{ display:"grid", gridTemplateColumns: isMobile ? "1fr" : isTablet ? "1fr 1fr" : "1.8fr 1fr 1fr 1fr 1fr", gap: isMobile ? "2.5rem" : "2rem", paddingBottom:"2.5rem", borderBottom:"1px solid rgba(15,23,42,0.079)" }}>
              <div>
                <PeepalLogo size={34} onClick={() => go("Home")}/>
                <p style={{ color:"#64748b", fontSize:".875rem", lineHeight:1.75, maxWidth:280, marginTop:"1.1rem", marginBottom:"1.25rem" }}>
                  Next-generation OSS · NMS solutions for operators and enterprises worldwide.
                </p>
                {/* Social */}
                <div style={{ display:"flex", gap:".65rem", marginTop:"1.25rem" }}>
                  {[
                    { label:"LinkedIn", href:"https://www.linkedin.com/company/peepalresearch/posts/?viewAsMember=true", icon:"in" },
                    { label:"Twitter", href:"https://twitter.com/peepalresearch", icon:"𝕏" },
                  ].map(s => (
                    <a key={s.label} href={s.href} target="_blank" rel="noopener noreferrer"
                      style={{ width:32, height:32, borderRadius:8, background:"rgba(15,23,42,0.067)", border:"1px solid rgba(15,23,42,0.102)", display:"flex", alignItems:"center", justifyContent:"center", color:"#475569", fontSize:".78rem", fontWeight:700, textDecoration:"none", transition:"all .2s" }}
                      onMouseEnter={e=>{e.currentTarget.style.background="rgba(250,161,45,0.1)";e.currentTarget.style.color="#96611b";}}
                      onMouseLeave={e=>{e.currentTarget.style.background="rgba(15,23,42,0.067)";e.currentTarget.style.color="#475569";}}>
                      {s.icon}
                    </a>
                  ))}
                </div>
              </div>

              {(isMobile
                ? [
                    { title:"Quick Links", links:[{l:"Home",p:"Home"},{l:"About",p:"About"},{l:"Solutions",p:"Solutions"},{l:"Careers",p:"Careers"},{l:"Contact",p:"Contact"}] },
                  ]
                : [
                    { title:"Company",  links:[{l:"Home",p:"Home"},{l:"About Us",p:"About"},{l:"Insights",p:"Insights"},{l:"Careers",p:"Contact"}] },
                    { title:"Products", links:[{l:"Request Demo",p:"Contact"},{l:"About Platform",p:"Solutions"}] },
                    { title:"Services", links:[{l:"Careers",p:"Careers"},{l:"NMS Integration",p:"Solutions"},{l:"Request Demo",p:"Contact"}] },
                    { title:"Contact",  links:[{l:"Get in Touch",p:"Contact"},{l:"info@peepalresearch.com",url:"mailto:info@peepalresearch.com"},{l:"Ottawa, Canada",p:"Contact"}] },
                  ]
              ).map(col => (
                <div key={col.title}>
                  <p role="heading" aria-level="3" style={{ color:"#0f172a", fontWeight:700, fontSize:".72rem", textTransform:"uppercase", letterSpacing:".1em", marginBottom:"1rem" }}>{col.title}</p>
                  {col.links.map(li => (
                    li.url
                      ? <a key={li.l} href={li.url} target={li.url.startsWith("http") ? "_blank" : "_self"} rel="noopener noreferrer" style={{ display:"block", color:"#64748b", fontSize:".875rem", marginBottom:".55rem", textDecoration:"none" }}
                          onMouseEnter={e=>e.currentTarget.style.color="#96611b"} onMouseLeave={e=>e.currentTarget.style.color="#64748b"}>{li.l}</a>
                      : <div key={li.l} style={{ color:"#64748b", fontSize:".875rem", marginBottom:".55rem", cursor:"pointer", transition:"color .2s" }}
                          onClick={() => li.p && go(li.p)}
                          onMouseEnter={e=>e.currentTarget.style.color="#96611b"} onMouseLeave={e=>e.currentTarget.style.color="#64748b"}>{li.l}</div>
                  ))}
                </div>
              ))}
            </div>
            <div style={{ paddingTop:"1.5rem", display:"flex", flexDirection: isMobile ? "column" : "row", justifyContent:"space-between", gap:".5rem", color:"#94a3b8", fontSize:".75rem" }}>
              <span>© 2026 Peepal Research Inc. All rights reserved.</span>
              <div style={{ display:"flex", gap:"1rem" }}>
                <span style={{ cursor:"pointer" }} onMouseEnter={e=>e.currentTarget.style.color="#96611b"} onMouseLeave={e=>e.currentTarget.style.color="#94a3b8"}>Privacy Policy</span>
                <span style={{ cursor:"pointer" }} onMouseEnter={e=>e.currentTarget.style.color="#96611b"} onMouseLeave={e=>e.currentTarget.style.color="#94a3b8"}>Terms of Service</span>
              </div>
            </div>
          </div>
        </footer>

        {/* ── AI CHAT ── */}
        <div style={{ position:"fixed", bottom: isMobile ? 16 : 28, right: isMobile ? 14 : 28, zIndex:9999 }}>
          {chatOpen && (
            <div style={{ position:"absolute", bottom:66, right:0, width: isMobile ? "calc(100vw - 28px)" : 350, borderRadius:18, overflow:"hidden", background:"#ffffff", border:"1px solid rgba(15,23,42,0.125)", boxShadow:"0 24px 70px rgba(0,0,0,0.195)", maxHeight:"80vh", display:"flex", flexDirection:"column" }}>
              {/* Header */}
              <div style={{ padding:"13px 16px", background:"linear-gradient(135deg,#faa12d,#f97316)", borderBottom:"1px solid rgba(15,23,42,0.09)", display:"flex", justifyContent:"space-between", alignItems:"center", flexShrink:0 }}>
                <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                  <div style={{ position:"relative" }}>
                    <PeepalLogo size={26} showText={false} />
                    <span style={{ position:"absolute", bottom:-1, right:-1, width:8, height:8, borderRadius:"50%", background:"#4ade80", border:"2px solid #ffffff" }}/>
                  </div>
                  <div>
                    <div style={{ fontWeight:700, fontSize:".85rem" }}>Peepal AI <span style={{ fontSize:".65rem", background:"rgba(250,161,45,.15)", color:"#96611b", padding:"1px 6px", borderRadius:4, fontWeight:600, marginLeft:4 }}>Claude</span></div>
                    <div style={{ fontSize:".65rem", color:"#475569" }}>OSS · NMS · Talent</div>
                  </div>
                </div>
                <button style={{ background:"none", border:"none", color:"#475569", fontSize:"1.4rem", cursor:"pointer", lineHeight:1, padding:"4px 8px" }} onClick={() => setChatOpen(false)}>×</button>
              </div>
              {/* Messages */}
              <div style={{ flex:1, overflowY:"auto", padding:"12px", display:"flex", flexDirection:"column", gap:9, minHeight:200, maxHeight:260 }}>
                {msgs.map((m,i) => (
                  <div key={i} style={{ display:"flex", justifyContent: m.from==="user" ? "flex-end" : "flex-start" }}>
                    <div style={{ padding:"9px 13px", borderRadius: m.from==="ai" ? "4px 13px 13px 13px" : "13px 4px 13px 13px", background: m.from==="ai" ? "rgba(15,23,42,0.067)" : "linear-gradient(135deg,#faa12d,#f97316)", color: m.from==="ai" ? "#cbd5e1" : "#fff", fontSize:".83rem", lineHeight:1.6, maxWidth:"85%" }}>
                      <ChatMd text={m.text} />
                    </div>
                  </div>
                ))}
                {typing && <div style={{ display:"flex" }}><div style={{ padding:"10px 14px", borderRadius:"4px 13px 13px 13px", background:"rgba(15,23,42,0.067)" }}><div className="dot-typing"><span/><span/><span/></div></div></div>}
                <div ref={chatEnd}/>
              </div>
              {/* Quick chips */}
              <div style={{ padding:"7px 10px", display:"flex", gap:5, overflowX:"auto", borderTop:"1px solid rgba(15,23,42,0.067)", flexShrink:0 }}>
                {["NMS","Talent","Pricing","Demo"].map(q => (
                  <button key={q} style={{ padding:"4px 10px", borderRadius:100, border:"1px solid rgba(250,161,45,.2)", background:"rgba(250,161,45,.06)", color:"#96611b", fontSize:".7rem", fontWeight:600, cursor:"pointer", whiteSpace:"nowrap", fontFamily:"inherit", flexShrink:0 }} onClick={() => setChatIn(q)}>
                    {q}
                  </button>
                ))}
              </div>
              {/* Web search toggle + input row */}
              <div style={{ padding:"8px 11px 10px", borderTop:"1px solid rgba(15,23,42,0.079)", display:"flex", flexDirection:"column", gap:7, flexShrink:0 }}>
                <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between" }}>
                  <button
                    onClick={() => setWebSearch(s => !s)}
                    style={{ display:"inline-flex", alignItems:"center", gap:5, padding:"5px 11px", borderRadius:20, border:`1px solid ${webSearch ? "rgba(56,189,248,.5)" : "rgba(15,23,42,0.148)"}`, background: webSearch ? "rgba(56,189,248,.1)" : "transparent", color: webSearch ? "#0369a1" : "#475569", fontSize:".72rem", fontWeight:700, cursor:"pointer", fontFamily:"inherit", transition:"all .2s" }}>
                    <span style={{ fontSize:".8rem" }}>{webSearch ? "🌐" : "🔍"}</span>
                    {webSearch ? "Web Search ON" : "Search Web"}
                  </button>
                  <span style={{ fontSize:".65rem", color:"#94a3b8" }}>Powered by Claude</span>
                </div>
                <div style={{ display:"flex", gap:7 }}>
                  <input value={chatIn} onChange={e => setChatIn(e.target.value)} onKeyDown={e => e.key==="Enter" && sendChat()} placeholder={webSearch ? "Search anything on the web…" : "Ask anything…"} style={{ flex:1, padding:"9px 13px", fontSize:15, borderRadius:9 }}/>
                  <button className="btn-p" style={{ padding:"9px 13px", borderRadius:9, flexShrink:0 }} onClick={sendChat}>→</button>
                </div>
              </div>
            </div>
          )}
          <button className="btn-p" onClick={() => setChatOpen(o => !o)}
            style={{ width:50, height:50, borderRadius:"50%", padding:0, fontSize:"1.3rem", boxShadow:"0 6px 28px rgba(250,161,45,.45)", position:"relative" }}>
            {chatOpen ? "×" : "🤖"}
            {!chatOpen && <span style={{ position:"absolute", top:2, right:2, width:10, height:10, borderRadius:"50%", background:"#4ade80", border:"2px solid #ffffff" }}/>}
          </button>
        </div>
      </div>
    </>
  );
}

/* ═══════════════ HOME ═══════════════ */
function HomePage({ isMobile, isTablet, setActivePage }) {
  return (
    <>
      {/* ── HERO + PLATFORM: unified intro ── */}
      <section style={{ position:"relative", background:"#ffffff", overflow:"hidden" }}>
        <NetworkBg/>
        <div style={{ position:"absolute", inset:0, background:"radial-gradient(ellipse at 60% 40%, rgba(250,161,45,0.06) 0%, transparent 55%)", zIndex:1 }}/>
        <div style={{ maxWidth:1300, margin:"0 auto", padding: isMobile ? "5rem 1.25rem 0" : "0 1.25rem", position:"relative", zIndex:2, width:"100%", minHeight: isMobile ? "auto" : "100vh", display:"grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: isMobile ? "3rem" : "4rem", alignItems:"center" }}>

          {/* Left: text */}
          <Reveal>
            <div>
              <div style={{ display:"inline-flex", alignItems:"center", gap:8, padding:"5px 14px", borderRadius:100, background:"rgba(250,161,45,.1)", border:"1px solid rgba(250,161,45,.25)", marginBottom:"1.75rem" }}>
                <span style={{ width:7, height:7, borderRadius:"50%", background:"#faa12d", display:"inline-block", animation:"pulse 2s infinite" }}/>
                <span style={{ color:"#96611b", fontSize: isMobile ? "0.72rem" : "1.1rem", fontWeight:700, letterSpacing:".06em", whiteSpace:"nowrap" }}>AI-NATIVE OSS · NMS → NetOrchestra AI</span>
              </div>
              <h1 style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize: isMobile ? "1.9rem" : "clamp(1.6rem,2.6vw,2.2rem)", fontWeight:900, lineHeight:1.08, letterSpacing:"-.03em", marginBottom:"1.5rem", color:"#0f172a" }}>
                See It. Find It. Fix It → <span style={{ background:"linear-gradient(135deg,#faa12d,#f97316)", WebkitBackgroundClip:"text", WebkitTextFillColor:"transparent" }}>Automatically.</span>
              </h1>
              <p style={{ fontSize: isMobile ? ".95rem" : "1.05rem", color:"#475569", lineHeight:1.85, marginBottom:"2.25rem", maxWidth:480 }}>
                We believe network operations should be autonomous. Peepal Research builds the AI-native platform that closes the loop — eliminating the NOC's manual burden by detecting, diagnosing, and remediating faults across any vendor, any protocol, around the clock.
              </p>
              <div style={{ display:"flex", gap:".85rem", flexWrap:"wrap", marginBottom: isMobile ? "2.5rem" : "3.5rem" }}>
                <button className="btn-p btn-blue" onClick={() => setActivePage("Solutions")}>Explore Solutions →</button>
                <button className="btn-p" onClick={() => setActivePage("Contact")}>Request a Demo →</button>
              </div>

            </div>
          </Reveal>

          {/* Right: product visual */}
          <Reveal delay={100}>
              <div style={{ position:"relative", display:"flex", justifyContent:"center" }}>
                <img src="/predictive-ai-autonomous-ops.png" alt="Predictive AI Autonomous Ops — vendor agnostic, predictive intelligence, explainable AI, human-in-the-loop, data sovereignty, autonomous remediation, zero-touch onboarding" style={{ display:"block", width:"100%", maxWidth:560, height:"auto", mixBlendMode:"multiply" }}/>
              </div>
            </Reveal>
        </div>

        {/* Product identity */}
        <div style={{ maxWidth:1300, margin:"0 auto", padding: isMobile ? "3rem 1.25rem 5rem" : "4rem 1.25rem 7rem", position:"relative", zIndex:2 }}>
          <Reveal>
            <div style={{ maxWidth:680 }}>
              <Tag size="1.1rem">AI-Native Platform</Tag>
              <h2 style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:"clamp(1.3rem,2.1vw,1.75rem)", fontWeight:800, color:"#0f172a", marginTop:".75rem", marginBottom:".5rem", lineHeight:1.2 }}>
                Unlock the full potential of your network infrastructure
              </h2>
              <p style={{ color:"#96611b", fontWeight:700, fontSize:".9rem", marginBottom:"1.5rem" }}>
                The platform built for autonomous network management
              </p>
              <p style={{ color:"#475569", lineHeight:1.85, marginBottom:"1rem", fontSize:".95rem" }}>
                Peepal Research is an Ottawa-based AI-native software company providing a unified platform for network management, automation, and assurance — built by telecom veterans with deep OSS/NMS expertise.
              </p>
              <p style={{ color:"#475569", lineHeight:1.85, marginBottom:"2rem", fontSize:".95rem" }}>
                Our platform autonomously detects, diagnoses, and remediates faults — reducing MTTR from hours to under 5 minutes, across any vendor, any protocol.
              </p>
              <div style={{ display:"flex", flexDirection:"column", gap:".85rem", marginBottom:"2rem" }}>
                {["Autonomous AI Remediation — 7-stage closed-loop fault healing","Multi-vendor adapter generation via LLM — no manual YANG coding","AIOps chat, anomaly detection, and SLA assurance built-in"].map(f => (
                  <div key={f} style={{ display:"flex", alignItems:"flex-start", gap:10, color:"#334155", fontSize:".9rem" }}>
                    <span style={{ color:"#96611b", marginTop:2, flexShrink:0 }}>✓</span>{f}
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
          <div style={{ textAlign:"center", marginTop: isMobile ? "1.75rem" : "2.5rem" }}>
            <button className="btn-p btn-blue" style={isMobile ? { width:"100%" } : {}} onClick={() => setActivePage("Solutions")}>Explore NetOrchestra AI →</button>
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section style={{ padding: isMobile ? "5rem 0" : "7rem 0", background:"#f1f5f9" }}>
        <div style={{ maxWidth:800, margin:"0 auto", padding:"0 1.25rem" }}>
          <Reveal>
            <div style={{ textAlign:"center", marginBottom:"3.5rem" }}>
              <Tag size="1.1rem">FAQ</Tag>
              <h2 style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:"clamp(1.35rem,2.2vw,1.85rem)", fontWeight:800, color:"#0f172a", marginTop:".75rem" }}>Common Questions</h2>
            </div>
          </Reveal>
          {FAQS.map((f,i) => <FaqItem key={i} faq={f} i={i}/>)}
        </div>
      </section>

      <CtaBanner setActivePage={setActivePage}/>
    </>
  );
}

/* ═══════════════ ABOUT ═══════════════ */
function AboutPage({ isMobile, isTablet, setActivePage }) {
  const TEAM = [
    { name:"Ravi Pandey", role:"Founder & CEO / CTO", initial:"R", color:"#96611b", photo:"/ravi-pandey.jpg",
      bullets:["23+ years: OSS/NMS, multi-vendor engineering, and network automation","Tier-1 Telco Proven: Nokia, Ciena, and Comverse production NMS platforms","Standards Expert: TMF APIs, gNMI/OpenConfig, and NETCONF/YANG","50+ Major Deployments across large-scale network automation projects"] },
    { name:"Pradeep Yadav", role:"Chief Operating Officer", initial:"P", color:"#0369a1", photo:"/pradeep-yadav.jpg",
      bullets:["21+ years of leadership in IT and Management consulting","Expert in architecting scalable operational frameworks","Proven revenue growth: from $50K to multi-million $ and team from 2 to 200","Transforms early-stage startups into scalable market leaders"] },
    { name:"Ravi Kumar", role:"Chief Strategy & Finance Officer", initial:"K", color:"#047857", photo:"/ravi-kumar.jpg",
      bullets:["40+ years of IT industry experience","Cognitive evangelist and innovation catalyst","Certified Management Consultant — strategy, value engineering, digital transformation","From product management to business consulting — technology to organizational performance"] },
  ];

  return (
    <>
      {/* Why "Peepal"? */}
      <section style={{ padding: isMobile ? "5rem 0 3.5rem" : "8rem 0 6rem", background:"linear-gradient(180deg,#ffffff,#f1f5f9)" }}>
        <div style={{ maxWidth:1300, margin:"0 auto", padding:"0 1.25rem" }}>
          <Reveal>
            <div style={{ textAlign:"center", marginBottom:"3.5rem" }}>
              <Tag size="1.1rem">Why "Peepal"?</Tag>
              <h2 style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:"clamp(1.35rem,2.2vw,1.85rem)", fontWeight:800, color:"#0f172a", marginTop:".75rem" }}>
                Roots at the top, growth flowing down
              </h2>
            </div>
          </Reveal>
          <div style={{ display:"grid", gridTemplateColumns: isMobile ? "1fr" : "1.15fr 0.85fr", gap: isMobile ? "2.5rem" : "4rem", alignItems:"center" }}>
            <Reveal>
              <div>
                <p style={{ color:"#475569", lineHeight:1.85, marginBottom:"1.1rem", fontSize:".95rem" }}>
                  The Peepal tree (Ficus religiosa) is a fitting analogy for a holding company — for how it structures itself, allocates capital, and manages risk.
                </p>
                <p style={{ color:"#475569", lineHeight:1.85, marginBottom:"1.1rem", fontSize:".95rem" }}>
                  Philosophically, the Peepal tree's roots sit in the upper realms, feeding life downward. A holding company works the same way: it doesn't produce consumer goods directly — its "roots" sit at the top of the corporate hierarchy, with strategy, governance, and capital allocation flowing down from the board of directors to the operating businesses below.
                </p>
                <p style={{ color:"#475569", lineHeight:1.85, fontSize:".95rem" }}>
                  At Peepal Research, our intent is to bring AI-native solutions to network infrastructure management. The first such product is NetOrchestra AI, with a slew of products slated for development in the future.
                </p>
              </div>
            </Reveal>
            {!isMobile && (
              <Reveal delay={100}>
                <div style={{ display:"flex", justifyContent:"center", alignItems:"center", padding:"2rem" }}>
                  <PeepalLogo size={220} showText={false}/>
                </div>
              </Reveal>
            )}
          </div>
        </div>
      </section>

      {/* Values */}
      <section style={{ padding: isMobile ? "5rem 0" : "7rem 0", background:"#f1f5f9" }}>
        <div style={{ maxWidth:1300, margin:"0 auto", padding:"0 1.25rem" }}>
          <Reveal>
            <div style={{ textAlign:"center", marginBottom:"3.5rem" }}>
              <Tag size="1.1rem">Our Values</Tag>
              <h2 style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:"clamp(1.35rem,2.2vw,1.85rem)", fontWeight:800, color:"#0f172a", marginTop:".75rem" }}>What drives us forward</h2>
            </div>
          </Reveal>
          <div style={{ display:"grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(4,1fr)", gap:"1.1rem" }}>
            {[
              {icon:"🚀",t:"Innovation",d:"GPT-4-class network reasoning built into the core. Our architecture is made for this moment."},
              {icon:"🤝",t:"Partnership",d:"Non-intrusive deployment. We co-exist with your incumbent NMS and cover the gaps from Day 1."},
              {icon:"🔓",t:"Openness",d:"Open standards first — TMF, gNMI, OpenConfig, NETCONF/YANG. No lock-in, ever."},
              {icon:"🎯",t:"Precision",d:"Three measurable success criteria agreed in writing before Day 1. MTTR delta reviewed weekly."},
            ].map((v,i) => (
              <Reveal key={v.t} delay={i*70}>
                <div style={{ padding:"1.75rem", borderRadius:14, background:"rgba(15,23,42,0.033)", border:"1px solid rgba(15,23,42,0.09)", textAlign:"center", height:"100%" }}>
                  <div style={{ fontSize:"2.2rem", marginBottom:"1rem" }}>{v.icon}</div>
                  <h3 style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontWeight:700, color:"#0f172a", marginBottom:".55rem", fontSize:"1.05rem" }}>{v.t}</h3>
                  <p style={{ color:"#475569", fontSize:".875rem", lineHeight:1.7 }}>{v.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Team */}
      <section style={{ padding: isMobile ? "5rem 0" : "7rem 0", background:"#ffffff" }}>
        <div style={{ maxWidth:1300, margin:"0 auto", padding:"0 1.25rem" }}>
          <Reveal>
            <div style={{ textAlign:"center", marginBottom:"3.5rem" }}>
              <Tag size="1.1rem">Core Team</Tag>
              <h2 style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:"clamp(1.35rem,2.2vw,1.85rem)", fontWeight:800, color:"#0f172a", marginTop:".75rem", marginBottom:"1.1rem" }}>
                Decades of hands-on telecom expertise
              </h2>
              <p style={{ color:"#475569", maxWidth:680, margin:"0 auto", fontSize:".95rem", lineHeight:1.8 }}>
                Our team at Peepal Research is made up of experienced leaders in the telecom, IT, and AI industry — a diverse, complementary group of professionals passionate about solving complex problems in network infrastructure management. Small, technical team. Real telecom deployments. Genuinely hard engineering. We are committed to staying at the forefront of innovation, giving our clients the best possible product and user experience.
              </p>
            </div>
          </Reveal>
          <div style={{ display:"grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr 1fr", gap:"1.5rem" }}>
            {TEAM.map((m,i) => (
              <Reveal key={m.name} delay={i*70}>
                <div style={{ padding:"2rem", borderRadius:14, background:"rgba(15,23,42,0.033)", border:"1px solid rgba(15,23,42,0.09)", height:"100%" }}>
                  <div style={{ width:80, height:80, borderRadius:"50%", overflow:"hidden", marginBottom:"1.1rem", border:`2px solid ${m.color}40`, background:`linear-gradient(135deg,${m.color}20,rgba(15,23,42,0.044))` }}>
                    {m.photo
                      ? <img src={m.photo} alt={m.name} style={{ width:"100%", height:"100%", objectFit:"cover", objectPosition:"top" }}/>
                      : <div style={{ width:"100%", height:"100%", display:"flex", alignItems:"center", justifyContent:"center", fontFamily:"'Plus Jakarta Sans',sans-serif", fontWeight:800, color:m.color, fontSize:"1.4rem" }}>{m.initial}</div>
                    }
                  </div>
                  <h3 style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontWeight:800, fontSize:"1.1rem", color:"#0f172a", marginBottom:".2rem" }}>{m.name}</h3>
                  <div style={{ color:m.color, fontSize:".8rem", fontWeight:600, marginBottom:"1.1rem" }}>{m.role}</div>
                  <div style={{ display:"flex", flexDirection:"column", gap:".55rem" }}>
                    {m.bullets.map(b => (
                      <div key={b} style={{ display:"flex", gap:8, color:"#475569", fontSize:".83rem", lineHeight:1.6 }}>
                        <span style={{ color:m.color, flexShrink:0, marginTop:2 }}>▸</span>{b}
                      </div>
                    ))}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CtaBanner setActivePage={setActivePage}/>
    </>
  );
}

/* ═══════════════ SOLUTIONS ═══════════════ */
function SolutionsPage({ isMobile, isTablet, setActivePage }) {
  const sol = SOLUTIONS[0];

  const PROBLEMS = [
    { icon:"🔒", title:"Vendor Lock-In Trap", stat:"$800K–$1.2M", statLabel:"avg. annual cost per incumbent NMS", desc:"Incumbent NMS platforms are built to manage one vendor's gear. Every additional vendor in the network means another six-figure NMS silo — separate teams, separate opex, zero correlation across the stack." },
    { icon:"⚡", title:"Reactive, Not Predictive", stat:"65%", statLabel:"of NOC tasks still manual", desc:"Legacy systems raise the alarm only after the outage hits. With no correlation and no root-cause insight, engineers are left sifting through alert floods — while SLAs quietly breach as tickets sit unassigned." },
    { icon:"🧱", title:"Complexity Wall", stat:"6–8 hrs", statLabel:"avg. MTTR on multi-vendor faults", desc:"Open RAN, network slicing, and constantly shifting topology have outgrown rules-based NMS built for a simpler era. Every fault still needs a human to review and approve before it can be resolved." },
  ];

  const TAILWINDS = [
    { n:"01", title:"5G Multi-Vendor Crisis", stat:"Fragmented", statLabel:"NMS estates across most carriers", desc:"Open RAN mandates pushed telcos off single-vendor networks for good. Most carriers now juggle multiple NMS platforms — separate teams, separate budgets, no shared view across any of them. We bring it all into one pane of glass." },
    { n:"02", title:"LLM Inflection Point", stat:"2024", statLabel:"first viable LLM telco automation", desc:"Production-grade AI reasoning over telecom networks only became viable in 2024. Runbooks, vendor MIBs, and 3GPP specs can now be embedded and queried in real time — and our architecture was built specifically for this moment." },
    { n:"03", title:"Regulatory Pressure (NIS2 / FCC)", stat:"$3.56B", statLabel:"NIS2 compliance market (2025) ↗", statLink:"https://www.factmr.com/report/nis2-and-cyber-resilience-act-compliance-services-market", desc:"New regulation across Europe and the US now mandates automated monitoring and incident-response documentation for critical network infrastructure. We deliver that compliance out of the box, not as an afterthought." },
  ];

  const BENEFITS = [
    { n:"01", title:"Autonomous Fault Remediation", desc:"Our 7-stage AI pipeline — Correlate → Govern → Plan → Blast Radius → Execute → Verify → Resolve — closes 76% of faults without human intervention. Average MTTR drops from 6–8 hours to under 5 minutes." },
    { n:"02", title:"LLM-Powered Adapter Agent AI", desc:"Automatically generate NETCONF/gNMI/REST adapters for Nokia SR Linux, Arista EOS, Juniper cRPD, Cisco IOS XR, and Ciena WaveServer using our LLM-powered Adapter Agent — zero manual YANG coding." },
    { n:"03", title:"AIOps Engine (LLM + RAG)", desc:"Query your network in natural language. Our AIOps engine embeds telco runbooks, vendor MIBs, and 3GPP specs — giving NOC engineers instant intelligent answers and alert correlation." },
    { n:"04", title:"TMF638/641/642 API Compliance", desc:"Built to the same standards Tier-1 telcos mandate for vendor onboarding. Integrates natively with ServiceNow, NetCracker, Amdocs, Jira/ITSM, IBM AIOps, HPE NNMi, and Ericsson OSS." },
    { n:"05", title:"Flexible, No-Lock-In Deployment", desc:"No enterprise licences, no multi-year lock-in — deploy alongside your existing NMS without disrupting what's already running." },
  ];

  const TRACTION = [
    { icon:"✓", title:"Complete platform", desc:"Built and running on a 13-node multi-vendor lab — a production-grade system." },
    { icon:"✓", title:"All AI modules live", desc:"AIOps assistant, autonomous remediation, and vendor adapter agent — each tested against real device telemetry." },
    { icon:"✓", title:"TMF638 / 641 / 642 API compliance", desc:"The same standard Tier-1 telcos mandate for vendor onboarding." },
    { icon:"✓", title:"Top Telecom Startup — SWC 2026 Invest Ottawa", desc:"Selected from hundreds of Ottawa startups to compete at Startup World Cup 2026." },
  ];

  return (
    <>
      <section style={{ padding: isMobile ? "5rem 0 3rem" : "8rem 0 4rem", background:"linear-gradient(180deg,#ffffff,#f1f5f9)", position:"relative", overflow:"hidden" }}>
        <NetworkBg/>
        <div style={{ maxWidth:1300, margin:"0 auto", padding:"0 1.25rem", position:"relative", zIndex:2, textAlign:"center" }}>
          <Reveal>
            <Tag size="1.1rem" noCaps>NetOrchestra AI</Tag>
            <h1 style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize: isMobile ? "2.1rem" : "clamp(1.6rem,2.6vw,2.2rem)", fontWeight:900, color:"#0f172a", letterSpacing:"-.02em", marginTop:"1.25rem", marginBottom:"1.1rem" }}>The AI-Native Network Management Platform</h1>
            <p style={{ color:"#475569", fontSize:".975rem", maxWidth:520, margin:"0 auto" }}>One autonomous platform to detect, diagnose, and remediate faults across your multi-vendor network end-to-end.</p>
          </Reveal>
        </div>
      </section>

      {/* Problem */}
      <section style={{ padding: isMobile ? "5rem 0" : "7rem 0", background:"#ffffff" }}>
        <div style={{ maxWidth:1300, margin:"0 auto", padding:"0 1.25rem" }}>
          <Reveal>
            <div style={{ textAlign:"center", marginBottom:"3.5rem" }}>
              <Tag color="#b91c1c" size="1.1rem">The Problem</Tag>
              <h2 style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize: "2rem", fontWeight:800, color:"#0f172a", marginTop:".75rem" }}>
                NOC teams are drowning in multi-vendor chaos
              </h2>
            </div>
          </Reveal>
          <div style={{ display:"grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr 1fr", gap:"1.5rem" }}>
            {PROBLEMS.map((p,i) => (
              <Reveal key={p.title} delay={i*70}>
                <div style={{ padding:"2rem", borderRadius:14, background:"rgba(239,68,68,0.04)", border:"1px solid rgba(239,68,68,0.12)", height:"100%" }}>
                  <div style={{ fontSize:"2rem", marginBottom:"1rem" }}>{p.icon}</div>
                  <h3 style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontWeight:700, fontSize:"1.05rem", color:"#0f172a", marginBottom:".5rem" }}>{p.title}</h3>
                  <div style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontWeight:900, fontSize:"1.6rem", color:"#b91c1c", marginBottom:".2rem" }}>{p.stat}</div>
                  <div style={{ fontSize:".75rem", color:"#475569", marginBottom:".9rem" }}>{p.statLabel}</div>
                  <p style={{ color:"#475569", fontSize:".875rem", lineHeight:1.7 }}>{p.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* NetOrchestra AI Solution */}
      <section style={{ padding: isMobile ? "5rem 0" : "7rem 0", background:"#f1f5f9" }}>
        <div style={{ maxWidth:1300, margin:"0 auto", padding:"0 1.25rem" }}>
          <Reveal>
            <div style={{ textAlign:"center", marginBottom:"3.5rem" }}>
              <Tag color="#047857" size="1.1rem">The Solution</Tag>
              <div style={{ color:sol.color, fontSize:".67rem", fontWeight:700, letterSpacing:".1em", textTransform:"uppercase", marginTop:".75rem", marginBottom:".55rem" }}>{sol.tagline}</div>
              <h2 style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize: "2rem", fontWeight:800, color:"#0f172a", marginBottom:"1.1rem" }}>{sol.name}</h2>
              <p style={{ color:"#475569", lineHeight:1.8, fontSize:".95rem", maxWidth:640, margin:"0 auto" }}>{sol.description}</p>
            </div>
          </Reveal>
          <div style={{ display:"grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: isMobile ? "2.5rem" : "5rem", alignItems:"center" }}>
            <Reveal>
              <div>
                <div style={{ display:"flex", flexDirection:"column", gap:".8rem", marginBottom:"2.25rem" }}>
                  {sol.features.map(f => (
                    <div key={f} style={{ display:"flex", alignItems:"center", gap:10, color:"#334155", fontSize:".875rem" }}>
                      <div style={{ width:22, height:22, borderRadius:6, background:`${sol.color}15`, display:"flex", alignItems:"center", justifyContent:"center", color:sol.color, fontSize:".75rem", flexShrink:0 }}>✓</div>{f}
                    </div>
                  ))}
                </div>
                <div style={{ textAlign: isMobile ? "left" : "center" }}>
                  <button className="btn-p" style={{ background:`linear-gradient(135deg,${sol.color},${sol.color}bb)` }} onClick={() => setActivePage("Contact")}>Request a Demo →</button>
                </div>
              </div>
            </Reveal>
            <Reveal>
              <div style={{ padding: isMobile ? "2rem" : "2.5rem", borderRadius:20, background:`linear-gradient(135deg,${sol.color}08,#ffffff)`, border:`1px solid ${sol.color}20`, position:"relative", overflow:"hidden" }}>
                <div style={{ fontSize:"3rem", marginBottom:"1.1rem" }}>{sol.emoji}</div>
                <h3 style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontWeight:700, fontSize:"1.3rem", color:"#0f172a", marginBottom:".9rem" }}>{sol.name} Capabilities</h3>
                <div style={{ display:"grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap:".8rem" }}>
                  {sol.features.map(f => (
                    <div key={f} style={{ padding:".75rem .9rem", borderRadius:8, background:"rgba(15,23,42,0.044)", border:"1px solid rgba(15,23,42,0.079)", fontSize:".78rem", color:"#475569" }}>
                      <span style={{ color:sol.color }}>◆ </span>{f}
                    </div>
                  ))}
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Why Now */}
      <section style={{ padding: isMobile ? "5rem 0" : "7rem 0", background:"#ffffff" }}>
        <div style={{ maxWidth:1300, margin:"0 auto", padding:"0 1.25rem" }}>
          <Reveal>
            <div style={{ textAlign:"center", marginBottom:"3.5rem" }}>
              <Tag size="1.1rem">Why Now?</Tag>
              <h2 style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize: "2rem", fontWeight:800, color:"#0f172a", marginTop:".75rem" }}>
                Three tailwinds converging right now
              </h2>
            </div>
          </Reveal>
          <div style={{ display:"grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr 1fr", gap:"1.5rem" }}>
            {TAILWINDS.map((t,i) => (
              <Reveal key={t.title} delay={i*70}>
                <div style={{ padding:"2rem", borderRadius:14, background:"rgba(15,23,42,0.033)", border:"1px solid rgba(15,23,42,0.09)", height:"100%" }}>
                  <div style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontWeight:900, fontSize:"1.8rem", color:"#f97316", marginBottom:".5rem" }}>{t.n}</div>
                  <h3 style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontWeight:700, fontSize:"1.05rem", color:"#0f172a", marginBottom:".5rem" }}>{t.title}</h3>
                  <div style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontWeight:900, fontSize:"1.5rem", color:"#96611b", marginBottom:".2rem" }}>{t.stat}</div>
                  {t.statLink
                    ? <a href={t.statLink} target="_blank" rel="noopener noreferrer" style={{ display:"block", fontSize:".75rem", color:"#475569", marginBottom:".9rem", textDecoration:"underline", textUnderlineOffset:2 }}>{t.statLabel}</a>
                    : <div style={{ fontSize:".75rem", color:"#475569", marginBottom:".9rem" }}>{t.statLabel}</div>}
                  <p style={{ color:"#475569", fontSize:".875rem", lineHeight:1.7 }}>{t.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Why It Works */}
      <section style={{ padding: isMobile ? "5rem 0" : "7rem 0", background:"#f1f5f9" }}>
        <div style={{ maxWidth:1300, margin:"0 auto", padding:"0 1.25rem" }}>
          <Reveal>
            <div style={{ textAlign:"center", marginBottom:"3.5rem" }}>
              <Tag size="1.1rem">Why It Works</Tag>
              <h2 style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize: "2rem", fontWeight:800, color:"#0f172a", marginTop:".75rem", marginBottom:".75rem" }}>
                Built for the realities of modern networks
              </h2>
              <p style={{ color:"#475569", maxWidth:500, margin:"0 auto", fontSize:".95rem", lineHeight:1.7 }}>
                Purpose-built for multi-vendor environments, cloud-native deployments, and autonomous operations.
              </p>
            </div>
          </Reveal>
          <div style={{ display:"grid", gridTemplateColumns: isMobile ? "1fr" : isTablet ? "1fr 1fr" : "1fr 1fr 1fr", gap:"1.5rem" }}>
            {BENEFITS.map((b,i) => (
              <Reveal key={b.title} delay={i*60}>
                <div style={{ padding:"1.75rem", borderRadius:14, background:"rgba(15,23,42,0.033)", border:"1px solid rgba(15,23,42,0.09)", height:"100%", transition:"transform 0.25s,box-shadow 0.25s" }}
                  onMouseEnter={e=>{e.currentTarget.style.transform="translateY(-4px)";e.currentTarget.style.boxShadow="0 16px 48px rgba(0,0,0,0.12)";}}
                  onMouseLeave={e=>{e.currentTarget.style.transform="none";e.currentTarget.style.boxShadow="none";}}>
                  <div style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:"2rem", fontWeight:900, color:"#f97316", marginBottom:".75rem" }}>{b.n}</div>
                  <h3 style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontWeight:700, fontSize:"1rem", color:"#0f172a", marginBottom:".6rem" }}>{b.title}</h3>
                  <p style={{ color:"#475569", fontSize:".875rem", lineHeight:1.7 }}>{b.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Traction */}
      <section style={{ padding: isMobile ? "5rem 0" : "7rem 0", background:"#ffffff" }}>
        <div style={{ maxWidth:1300, margin:"0 auto", padding:"0 1.25rem" }}>
          <Reveal>
            <div style={{ textAlign:"center", marginBottom:"3.5rem" }}>
              <Tag color="#047857" size="1.1rem">Traction & Validation</Tag>
              <h2 style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize: "2rem", fontWeight:800, color:"#0f172a", marginTop:".75rem" }}>
                What we've built — and what the market confirms
              </h2>
            </div>
          </Reveal>
          <Reveal>
            <div style={{ display:"grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: isMobile ? "3rem" : "6rem", alignItems:"center" }}>
              <div style={{ display:"flex", flexDirection:"column", gap:"1rem" }}>
                {TRACTION.map(t => (
                  <div key={t.title} style={{ display:"flex", gap:".85rem", padding:"1rem 1.25rem", borderRadius:10, background:"rgba(52,211,153,0.05)", border:"1px solid rgba(52,211,153,0.12)" }}>
                    <span style={{ color:"#047857", fontWeight:700, flexShrink:0 }}>{t.icon}</span>
                    <div>
                      <div style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontWeight:700, color:"#0f172a", fontSize:".92rem", marginBottom:".2rem" }}>{t.title}</div>
                      <div style={{ color:"#475569", fontSize:".83rem" }}>{t.desc}</div>
                    </div>
                  </div>
                ))}
              </div>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"1rem" }}>
                {[
                  { stat:"$24.7B→$54B", label:"OSS/BSS Market", sub:"2025 → 2031 (Mordor Intelligence) ↗", link:"https://www.mordorintelligence.com/industry-reports/oss-bss-market" },
                  { stat:"30%", label:"Enterprises Automating >50% of Networks", sub:"by 2026 (Gartner) ↗", link:"https://www.gartner.com/en/newsroom/press-releases/2024-09-18-gartner-says-30-percent-of-enterprises-will-automate-more-than-half-of-their-network-activities-by-2026" },
                  { stat:"72%", label:"Say Their AI Is Trustworthy", sub:"but only 14% can prove it — TM Forum ↗", link:"https://www.tmforum.org/news-insight/newsroom/tm-forum-reveals-ai-trust-gap-72-of-csps-say-their-ai-is-trustworthy,-only-14-can-prove-it" },
                  { stat:"76%", label:"Faults Auto-Closed", sub:"by NetOrchestra AI pipeline" },
                ].map(s => (
                  <div key={s.label} style={{ padding:"1.25rem", borderRadius:12, background:"rgba(15,23,42,0.044)", border:"1px solid rgba(15,23,42,0.09)", textAlign:"center" }}>
                    <div style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontWeight:900, fontSize:"1.4rem", color:"#96611b", marginBottom:".3rem" }}>{s.stat}</div>
                    <div style={{ color:"#0f172a", fontSize:".8rem", fontWeight:600, marginBottom:".2rem" }}>{s.label}</div>
                    {s.link
                      ? <a href={s.link} target="_blank" rel="noopener noreferrer" style={{ color:"#64748b", fontSize:".7rem", textDecoration:"underline", textUnderlineOffset:2 }}>{s.sub}</a>
                      : <div style={{ color:"#64748b", fontSize:".7rem" }}>{s.sub}</div>}
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section style={{ padding:"3.5rem 0", background:"#f1f5f9", borderTop:"1px solid rgba(15,23,42,0.067)" }}>
        <div style={{ maxWidth:1300, margin:"0 auto", padding:"0 1.25rem", textAlign:"center" }}>
          <p style={{ color:"#475569", fontSize:".76rem", letterSpacing:".12em", textTransform:"uppercase", marginBottom:"2rem" }}>Integrates with your existing ecosystem</p>
          <div style={{ display:"flex", justifyContent:"center", gap: isMobile ? "1.25rem" : "2.5rem", flexWrap:"wrap" }}>
            {["Cisco","Nokia","Ericsson","Juniper","Huawei","AWS","Oracle OCI","TM Forum"].map(p => (
              <div key={p} style={{ color:"#0f172a", fontSize:".88rem", fontWeight:700, fontFamily:"'Plus Jakarta Sans',sans-serif" }}>{p}</div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}

/* ═══════════════ TALENT ═══════════════ */
function CareersPage({ isMobile, isTablet, setActivePage }) {
  const OPENINGS = [
    {
      id:"cse-001",
      title:"Customer Support Engineer (CSE)",
      type:"Full-Time · Remote / Ottawa",
      dept:"Customer Success",
      color:"#047857",
      summary:"You will be the technical backbone of NetOrchestra AI's first customer deployments — owning onboarding, configuration, and day-to-day operational support for Tier-2/3 telcos and MSPs running our AI-native NMS platform.",
      responsibilities:[
        "Lead end-to-end onboarding of new customers onto NetOrchestra AI — from environment setup through to first autonomous fault remediation",
        "Configure and validate vendor adapters (Nokia SR Linux, Arista EOS, Juniper cRPD, Cisco IOS XR, Ciena WaveServer) using gNMI/NETCONF/REST",
        "Monitor deployed instances, triage alerts, and escalate anomalies to engineering with full diagnostic context",
        "Produce and maintain customer-facing runbooks, configuration guides, and API integration documentation",
        "Participate in weekly MTTR delta reviews with customers and report on go/no-go criteria to the Account Executive",
        "Collaborate with engineering to reproduce bugs, validate fixes, and test new releases in lab environments",
        "Respond to P1/P2 incidents within SLA windows and drive resolution with internal teams",
        "Gather customer feedback and translate it into product improvement requests for the engineering roadmap",
      ],
      requirements:[
        "3+ years in a NOC, network operations, or technical support role within a telecom or MSP environment",
        "Hands-on experience with at least two of: Nokia SR Linux / NetAct, Juniper Junos, Arista EOS, Cisco IOS XR, Ciena WaveServer",
        "Working knowledge of gNMI, NETCONF/YANG, and REST/RESTConf protocols",
        "Familiarity with OSS/NMS platforms (HP OpenView, IBM Netcool, Zabbix, or equivalent)",
        "Understanding of IP/MPLS, BGP, OSPF, and optical transport fundamentals",
        "Experience reading and writing basic Python or Bash scripts for automation tasks",
        "Strong written and verbal communication — you will author customer-facing documentation",
        "Ability to work independently in a fast-moving startup environment",
      ],
      nice:[
        "Experience with TMF APIs (TMF638, TMF641, TMF642)",
        "Exposure to Kubernetes/Docker for containerised network functions",
        "Familiarity with TimescaleDB, InfluxDB, or streaming platforms",
        "CCNA, JNCIA, or equivalent vendor certification",
      ],
    },
    {
      id:"ae-001",
      title:"Account Executive",
      type:"Full-Time · Remote / Ottawa",
      dept:"Sales",
      color:"#96611b",
      summary:"You will drive NetOrchestra AI's first commercial revenue — owning the full sales cycle from prospecting through close for Tier-2/3 telcos, regional carriers, and MSP network operators across North America and Europe.",
      responsibilities:[
        "Own the full sales cycle for NetOrchestra AI: prospecting, discovery, technical demo, pilot proposal, negotiation, and close",
        "Identify and qualify Tier-2/3 telcos, regional carriers, and MSP operators experiencing multi-vendor NMS complexity",
        "Deliver compelling value-based demonstrations tailored to NOC managers, network architects, and CTOs",
        "Structure and negotiate Pilot agreements including defined success criteria, MTTR baselines, and go/no-go gates",
        "Maintain accurate pipeline hygiene in CRM with weekly forecasting and stage progression",
        "Partner with the CSE to ensure seamless handoff from signed Pilot to successful deployment",
        "Represent Peepal Research at industry events, telecom conferences, and partner programs",
        "Build and manage a network of channel partners, VARs, and MSP white-label relationships",
      ],
      requirements:[
        "5+ years of B2B enterprise software sales, with at least 2 years selling into telecom operators or MSPs",
        "Proven track record closing deals $25K–$250K+ ARR with 6–12 month sales cycles",
        "Strong understanding of network operations, NOC workflows, and the OSS/NMS landscape",
        "Ability to present confidently to technical audiences (engineers, architects) and business stakeholders (CTO, CFO)",
        "Experience with value-based selling, ROI frameworks, and competitive displacement",
        "Self-starter comfortable building pipeline from scratch in a pre-revenue startup environment",
        "Proficiency with CRM tools (Salesforce, HubSpot, or equivalent)",
      ],
      nice:[
        "Existing relationships with procurement teams at Canadian or European telcos",
        "Experience selling AI/ML-driven network management or automation platforms",
        "Understanding of NIS2, FCC critical-infrastructure, or TM Forum compliance requirements",
        "Familiarity with MSP white-label sales models",
      ],
    },
  ];

  const [openJob, setOpenJob] = useState(null);

  return (
    <>
      <section style={{ padding: isMobile ? "5rem 0 3rem" : "8rem 0 5rem", background:"linear-gradient(180deg,#ffffff,#f1f5f9)", position:"relative", overflow:"hidden" }}>
        <NetworkBg/>
        <div style={{ maxWidth:1300, margin:"0 auto", padding:"0 1.25rem", position:"relative", zIndex:2 }}>
          <Reveal>
            <Tag size="1.1rem">Careers at Peepal Research</Tag>
            <h1 style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize: isMobile ? "1.25rem" : isTablet ? "1.35rem" : "clamp(1.1rem,1.9vw,1.7rem)", fontWeight:900, color:"#0f172a", letterSpacing:"-.02em", lineHeight:1.25, marginTop:"1.25rem", marginBottom:"1.1rem" }}>
              Help us build the future of autonomous network management
            </h1>
            <p style={{ color:"#475569", fontSize:".95rem", maxWidth:500, lineHeight:1.8, marginBottom:"1.5rem" }}>
              An exciting career awaits — join our growing team.
            </p>
          </Reveal>
        </div>
      </section>

      <section style={{ padding: isMobile ? "4rem 0 5rem" : "5rem 0 7rem", background:"#ffffff" }}>
        <div style={{ maxWidth:900, margin:"0 auto", padding:"0 1.25rem" }}>
          <Reveal>
            <Tag size="1.1rem">We're Hiring</Tag>
            <h2 style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:"clamp(1.35rem,2.2vw,1.85rem)", fontWeight:800, color:"#0f172a", marginTop:".75rem", marginBottom:"2.5rem" }}>
              Open Positions
            </h2>
          </Reveal>
          <div style={{ display:"flex", flexDirection:"column", gap:"1.25rem" }}>
            {OPENINGS.map((job,i) => (
              <Reveal key={job.id} delay={i*60}>
                <div style={{ borderRadius:14, border:`1px solid ${openJob===job.id ? job.color+"40" : "rgba(15,23,42,0.102)"}`, background:"rgba(15,23,42,0.033)", overflow:"hidden", transition:"border-color 0.2s" }}>
                  <div style={{ padding:"1.5rem 1.75rem", display:"flex", alignItems:"center", justifyContent:"space-between", gap:"1rem", flexWrap:"wrap", cursor:"pointer" }}
                    onClick={() => setOpenJob(openJob===job.id ? null : job.id)}>
                    <div>
                      <div style={{ display:"flex", alignItems:"center", gap:".6rem", marginBottom:".4rem" }}>
                        <span style={{ padding:"3px 10px", borderRadius:100, fontSize:".65rem", fontWeight:700, letterSpacing:".08em", textTransform:"uppercase", background:`${job.color}15`, color:job.color, border:`1px solid ${job.color}30` }}>{job.dept}</span>
                        <span style={{ color:"#64748b", fontSize:".78rem" }}>{job.type}</span>
                      </div>
                      <h3 style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontWeight:800, fontSize:"1.05rem", color:"#0f172a" }}>{job.title}</h3>
                    </div>
                    <div style={{ display:"flex", alignItems:"center", gap:".75rem" }}>
                      <button className="btn-p" style={{ padding:"9px 22px", fontSize:".82rem" }}
                        onClick={e => { e.stopPropagation(); setActivePage("Contact"); }}>
                        Apply →
                      </button>
                      <span style={{ color:"#64748b", fontSize:"1.2rem", display:"inline-block", transform: openJob===job.id ? "rotate(180deg)" : "none", transition:"transform 0.2s" }}>⌄</span>
                    </div>
                  </div>
                  {openJob===job.id && (
                    <div style={{ padding:"0 1.75rem 1.75rem", borderTop:"1px solid rgba(15,23,42,0.079)" }}>
                      <p style={{ color:"#334155", lineHeight:1.8, margin:"1.25rem 0 1.75rem", fontSize:".9rem" }}>{job.summary}</p>
                      {[["Responsibilities",job.responsibilities,job.color],["Requirements",job.requirements,job.color],["Nice to Have",job.nice,"#475569"]].map(([heading,items,col]) => (
                        <div key={heading} style={{ marginBottom:"1.5rem" }}>
                          <h4 style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontWeight:700, fontSize:".82rem", color:"#0f172a", marginBottom:".75rem", textTransform:"uppercase", letterSpacing:".06em" }}>{heading}</h4>
                          <div style={{ display:"flex", flexDirection:"column", gap:".5rem" }}>
                            {items.map(it => (
                              <div key={it} style={{ display:"flex", gap:".65rem", color:"#475569", fontSize:".875rem", lineHeight:1.65 }}>
                                <span style={{ color:col, flexShrink:0, marginTop:2 }}>▸</span>{it}
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                      <div style={{ marginTop:"1.5rem", padding:"1rem 1.25rem", borderRadius:9, background:"rgba(250,161,45,0.06)", border:"1px solid rgba(250,161,45,0.15)" }}>
                        <p style={{ color:"#334155", fontSize:".85rem", lineHeight:1.7 }}>
                          <strong style={{ color:"#96611b" }}>To apply:</strong> Send your resume and a brief note to <a href="mailto:careers@peepalresearch.com" style={{ color:"#96611b" }}>careers@peepalresearch.com</a> or click Apply above. We respond to every application within 5 business days.
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section style={{ padding: isMobile ? "5rem 0" : "7rem 0", background:"#f1f5f9" }}>
        <div style={{ maxWidth:1300, margin:"0 auto", padding:"0 1.25rem" }}>
          <Reveal>
            <div style={{ textAlign:"center", marginBottom:"3rem" }}>
              <Tag>Why Join Us</Tag>
              <h2 style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:"clamp(1.35rem,2.2vw,1.85rem)", fontWeight:800, color:"#0f172a", marginTop:".75rem" }}>What it is like to work at Peepal Research</h2>
            </div>
          </Reveal>
          <div style={{ display:"grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(3,1fr)", gap:"1.1rem" }}>
            {[
              {icon:"🛠️",t:"Real Impact",d:"Your work ships to production telecom environments. You will see your code managing live networks."},
              {icon:"🧠",t:"Hard Problems",d:"Autonomous fault remediation, LLM-based adapters, multi-vendor telemetry at scale. Genuinely difficult work."},
              {icon:"🌐",t:"Remote-Friendly",d:"Ottawa HQ with full remote flexibility. We care about output, not office hours."},
            ].map((v,i) => (
              <Reveal key={v.t} delay={i*60}>
                <div style={{ padding:"1.5rem", borderRadius:12, background:"rgba(15,23,42,0.033)", border:"1px solid rgba(15,23,42,0.09)", textAlign:"center" }}>
                  <div style={{ fontSize:"1.8rem", marginBottom:".75rem" }}>{v.icon}</div>
                  <h3 style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontWeight:700, color:"#0f172a", marginBottom:".5rem", fontSize:".95rem" }}>{v.t}</h3>
                  <p style={{ color:"#475569", fontSize:".82rem", lineHeight:1.65 }}>{v.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CtaBanner setActivePage={setActivePage} heading="Do not see your role?" sub="We are always open to exceptional people. Send your CV to careers@peepalresearch.com." label="Get in Touch"/>
    </>
  );
}


/* ═══════════════ BLOG ARTICLE VIEW ═══════════════ */
function BlogArticle({ post, onBack, isMobile }) {
  useEffect(() => { window.scrollTo(0,0); }, []);
  const tagColors = { "Press":"#96611b","AI + OSS":"#0369a1","5G Inventory":"#047857","NMS":"#047857","Automation":"#96611b","Careers":"#6d28d9" };
  const tc = tagColors[post.tag] || "#96611b";
  return (
    <>
      {/* Hero */}
      <div style={{ position:"relative", height: isMobile ? 220 : 360, overflow:"hidden" }}>
        <img src={post.img} alt={post.title} style={{ width:"100%", height:"100%", objectFit:"cover" }}/>
        <div style={{ position:"absolute", inset:0, background:"linear-gradient(to bottom, rgba(5,13,26,0.35) 0%, rgba(5,13,26,0.92) 100%)" }}/>
        <div style={{ position:"absolute", bottom:0, left:0, right:0, padding: isMobile ? "1.5rem 1.25rem" : "3rem", maxWidth:860, margin:"0 auto" }}>
          <button onClick={onBack} style={{ background:"rgba(15,23,42,0.102)", border:"1px solid rgba(15,23,42,0.182)", color:"#334155", borderRadius:7, padding:"6px 14px", fontSize:".8rem", cursor:"pointer", fontFamily:"inherit", marginBottom:"1rem", display:"inline-flex", alignItems:"center", gap:5 }}>
            ← Back to Insights
          </button>
          <div style={{ display:"flex", gap:".75rem", alignItems:"center", marginBottom:".9rem" }}>
            <Tag color={tc}>{post.tag}</Tag>
            <span style={{ color:"#475569", fontSize:".78rem" }}>{post.date} · {post.readTime} read</span>
          </div>
          <h1 style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize: isMobile ? "1.55rem" : "2.5rem", fontWeight:900, color:"#0f172a", lineHeight:1.15, letterSpacing:"-.02em", margin:0 }}>{post.title}</h1>
        </div>
      </div>

      {/* Article body */}
      <div style={{ background:"#ffffff", padding: isMobile ? "2.5rem 0 5rem" : "4rem 0 7rem" }}>
        <div style={{ maxWidth:760, margin:"0 auto", padding:"0 1.25rem" }}>
          {/* Lead excerpt */}
          <p style={{ fontSize: isMobile ? "1.05rem" : "1.2rem", color:"#334155", lineHeight:1.8, marginBottom:"2.5rem", paddingBottom:"2rem", borderBottom:"1px solid rgba(15,23,42,0.09)", fontStyle:"italic" }}>{post.excerpt}</p>

          {/* Content blocks */}
          {(post.content || []).map((block, i) => {
            if (block.type === "h2") return (
              <h2 key={i} style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize: isMobile ? "1.2rem" : "1.45rem", fontWeight:800, color:"#0f172a", margin:"2.5rem 0 1rem", lineHeight:1.25 }}>{block.text}</h2>
            );
            if (block.type === "p") return (
              <p key={i} style={{ color:"#334155", lineHeight:1.85, fontSize: isMobile ? ".95rem" : "1rem", marginBottom:"1.4rem" }}>{block.text}</p>
            );
            if (block.type === "ul") return (
              <ul key={i} style={{ color:"#334155", lineHeight:1.85, fontSize: isMobile ? ".95rem" : "1rem", marginBottom:"1.4rem", paddingLeft:"1.5rem" }}>
                {block.items.map((item, j) => <li key={j} style={{ marginBottom:".5rem" }}>{item}</li>)}
              </ul>
            );
            return null;
          })}

          {/* Back + related */}
          <div style={{ marginTop:"3.5rem", paddingTop:"2.5rem", borderTop:"1px solid rgba(15,23,42,0.09)", display:"flex", justifyContent:"space-between", alignItems:"center", flexWrap:"wrap", gap:"1rem" }}>
            <button onClick={onBack} style={{ display:"inline-flex", alignItems:"center", gap:6, padding:"10px 20px", borderRadius:8, border:"1px solid rgba(15,23,42,0.182)", background:"transparent", color:"#334155", cursor:"pointer", fontFamily:"inherit", fontSize:".875rem" }}>
              ← All Articles
            </button>
            <span style={{ color:"#94a3b8", fontSize:".78rem" }}>© 2026 Peepal Research Inc.</span>
          </div>
        </div>
      </div>
    </>
  );
}

/* ═══════════════ INSIGHTS ═══════════════ */
function InsightsPage({ isMobile }) {
  const [openPost, setOpenPost] = useState(null);
  const isTablet = useIsTablet();
  const tagColors = { "Press":"#96611b","AI + OSS":"#0369a1","5G Inventory":"#047857","NMS":"#047857","Automation":"#96611b","Careers":"#6d28d9" };

  if (openPost !== null) {
    return <BlogArticle post={BLOGS[openPost]} onBack={() => { setOpenPost(null); window.scrollTo(0,0); }} isMobile={isMobile}/>;
  }

  const [featured, ...rest] = BLOGS;

  return (
    <>
      {/* Hero */}
      <section style={{ padding: isMobile ? "5rem 0 3.5rem" : "8rem 0 6rem", background:"linear-gradient(180deg,#ffffff,#f1f5f9)", position:"relative", overflow:"hidden" }}>
        <NetworkBg/>
        <div style={{ maxWidth:1300, margin:"0 auto", padding:"0 1.25rem", position:"relative", zIndex:2 }}>
          <Reveal>
            <Tag size="1.1rem">Insights & Research</Tag>
            <h1 style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize: isMobile ? "1.7rem" : isTablet ? "1.6rem" : "clamp(1.4rem,2.2vw,2rem)", fontWeight:900, color:"#0f172a", letterSpacing:"-.02em", marginTop:"1.25rem", lineHeight:1.2 }}>
              Thinking ahead on network intelligence
            </h1>
            <p style={{ color:"#475569", fontSize:".975rem", marginTop:"1rem", maxWidth:480, lineHeight:1.75 }}>In-depth articles on OSS, NMS, 5G automation, and telecom talent — written by the engineers who build the tools.</p>
          </Reveal>
        </div>
      </section>

      {/* Featured post */}
      <section style={{ padding: isMobile ? "3rem 0 1rem" : "5rem 0 2rem", background:"#ffffff" }}>
        <div style={{ maxWidth:1300, margin:"0 auto", padding:"0 1.25rem" }}>
          <Reveal>
            <div style={{ display:"grid", gridTemplateColumns: isMobile ? "1fr" : "1.35fr 1fr", gap: isMobile ? "0" : "0", borderRadius:18, overflow:"hidden", border:"1px solid rgba(15,23,42,0.09)", cursor:"pointer", background:"rgba(15,23,42,0.033)" }}
              onClick={() => featured.externalUrl ? window.open(featured.externalUrl, "_blank", "noopener noreferrer") : setOpenPost(0)}>
              <div style={{ height: isMobile ? 220 : 380, overflow:"hidden", position:"relative" }}>
                <img src={featured.img} alt={featured.title} style={{ width:"100%", height:"100%", objectFit:"cover", transition:"transform .4s" }}
                  onMouseEnter={e => e.target.style.transform="scale(1.04)"} onMouseLeave={e => e.target.style.transform="scale(1)"}/>
                <div style={{ position:"absolute", inset:0, background:"linear-gradient(to right, transparent 60%, rgba(5,13,26,0.8))" }}/>
              </div>
              <div style={{ padding: isMobile ? "1.75rem" : "3rem", display:"flex", flexDirection:"column", justifyContent:"center", background:"rgba(15,23,42,0.033)" }}>
                <div style={{ display:"flex", gap:".75rem", alignItems:"center", marginBottom:"1rem" }}>
                  <Tag color={tagColors[featured.tag] || "#96611b"}>{featured.tag}</Tag>
                  <span style={{ background:"rgba(250,161,45,.1)", color:"#96611b", fontSize:".7rem", fontWeight:700, padding:"3px 9px", borderRadius:4 }}>Featured</span>
                </div>
                <h2 style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontWeight:800, fontSize: isMobile ? "1.3rem" : "1.65rem", color:"#0f172a", lineHeight:1.25, marginBottom:".9rem" }}>{featured.title}</h2>
                <p style={{ color:"#475569", lineHeight:1.75, fontSize:".9rem", marginBottom:"1.5rem", flexGrow:1 }}>{featured.excerpt}</p>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                  <span style={{ color:"#64748b", fontSize:".76rem" }}>{featured.date} · {featured.readTime}</span>
                  <span style={{ color:"#96611b", fontWeight:700, fontSize:".88rem" }}>{featured.externalUrl ? "Read on Invest Ottawa ↗" : "Read article →"}</span>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Rest of posts grid */}
      <section style={{ padding: isMobile ? "2.5rem 0 5rem" : "4rem 0 7rem", background:"#ffffff" }}>
        <div style={{ maxWidth:1300, margin:"0 auto", padding:"0 1.25rem" }}>
          <div style={{ display:"grid", gridTemplateColumns: isMobile ? "1fr" : isTablet ? "1fr 1fr" : "1fr 1fr 1fr", gap:"1.5rem" }}>
            {rest.map((post, i) => (
              <Reveal key={post.title} delay={i*60}>
                <div style={{ borderRadius:14, overflow:"hidden", background:"rgba(15,23,42,0.033)", border:"1px solid rgba(15,23,42,0.09)", cursor:"pointer", display:"flex", flexDirection:"column", transition:"transform .25s, box-shadow .25s" }}
                  onClick={() => post.externalUrl ? window.open(post.externalUrl, "_blank", "noopener noreferrer") : setOpenPost(i+1)}
                  onMouseEnter={e => { e.currentTarget.style.transform="translateY(-4px)"; e.currentTarget.style.boxShadow="0 16px 40px rgba(0,0,0,0.12)"; }}
                  onMouseLeave={e => { e.currentTarget.style.transform="none"; e.currentTarget.style.boxShadow="none"; }}>
                  <div style={{ height:175, overflow:"hidden", position:"relative", flexShrink:0 }}>
                    <img src={post.img} alt={post.title} style={{ width:"100%", height:"100%", objectFit:"cover", transition:"transform .4s" }}
                      onMouseEnter={e => e.target.style.transform="scale(1.05)"} onMouseLeave={e => e.target.style.transform="scale(1)"}/>
                    <div style={{ position:"absolute", inset:0, background:"linear-gradient(to top,rgba(5,13,26,0.75),transparent)" }}/>
                  </div>
                  <div style={{ padding:"1.4rem", display:"flex", flexDirection:"column", flex:1 }}>
                    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:".8rem" }}>
                      <Tag color={tagColors[post.tag] || "#96611b"}>{post.tag}</Tag>
                      <span style={{ color:"#64748b", fontSize:".73rem" }}>{post.readTime}</span>
                    </div>
                    <h3 style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontWeight:700, fontSize:"1rem", color:"#0f172a", lineHeight:1.4, marginBottom:".55rem", flex:1 }}>{post.title}</h3>
                    <p style={{ color:"#475569", fontSize:".84rem", lineHeight:1.65, marginBottom:"1rem", display:"-webkit-box", WebkitLineClamp:3, WebkitBoxOrient:"vertical", overflow:"hidden" }}>{post.excerpt}</p>
                    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginTop:"auto" }}>
                      <span style={{ color:"#64748b", fontSize:".73rem" }}>{post.date}</span>
                      <span style={{ color:"#96611b", fontSize:".83rem", fontWeight:700 }}>{post.externalUrl ? "Read ↗" : "Read →"}</span>
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}

/* ═══════════════ CONTACT ═══════════════ */
function ContactPage({ isMobile, setActivePage, form, setForm, formSt, submitContact }) {
  return (
    <>
      <section style={{ padding: isMobile ? "5rem 0 3rem" : "8rem 0 4rem", background:"linear-gradient(180deg,#ffffff,#f1f5f9)", position:"relative", overflow:"hidden" }}>
        <NetworkBg/>
        <div style={{ maxWidth:1300, margin:"0 auto", padding:"0 1.25rem", position:"relative", zIndex:2, textAlign:"center" }}>
          <Reveal>
            <Tag size="1.1rem">Get in Touch</Tag>
            <h1 style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize: isMobile ? "2.1rem" : "clamp(1.6rem,2.6vw,2.2rem)", fontWeight:900, color:"#0f172a", letterSpacing:"-.02em", marginTop:"1.25rem", marginBottom:"1.1rem" }}>
              Let's build the future<br/>of your network
            </h1>
            <p style={{ color:"#475569", fontSize:".975rem", maxWidth:460, margin:"0 auto" }}>Product demo, OSS consulting, or telecom talent — our team responds within one business day.</p>
          </Reveal>
        </div>
      </section>

      <section style={{ padding: isMobile ? "3rem 0 5rem" : "6rem 0 8rem", background:"#ffffff" }}>
        <div style={{ maxWidth:1040, margin:"0 auto", padding:"0 1.25rem" }}>
          <div style={{ display:"grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1.5fr", gap: isMobile ? "3rem" : "5rem", alignItems:"start" }}>

            {/* Left */}
            <Reveal>
              <div>
                <h2 style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontWeight:800, fontSize:"1.45rem", color:"#0f172a", marginBottom:"1.6rem" }}>Contact Information</h2>
                {[{ icon:"✉️", label:"Email", value:"info@peepalresearch.com" },{ icon:"📍", label:"Headquarters", value:"Unit 104, 50 Frank Nighbor Pl, Ottawa, ON, K2V 1B9, Canada" },{ icon:"💼", label:"Business Hours", value:"Mon–Fri, 9am–6pm EST" }].map(c => (
                  <div key={c.label} style={{ display:"flex", gap:".85rem", marginBottom:"1.5rem" }}>
                    <div style={{ width:42, height:42, borderRadius:10, background:"rgba(250,161,45,0.1)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:"1.1rem", flexShrink:0 }}>{c.icon}</div>
                    <div><div style={{ color:"#64748b", fontSize:".76rem", marginBottom:3 }}>{c.label}</div><div style={{ color:"#0f172a", fontWeight:500, fontSize:".9rem" }}>{c.value}</div></div>
                  </div>
                ))}
              </div>
            </Reveal>

            {/* Right form */}
            <Reveal>
              {formSt === "sent" ? (
                <div style={{ padding:"3rem 1.5rem", borderRadius:18, background:"rgba(52,211,153,0.06)", border:"1px solid rgba(52,211,153,0.2)", textAlign:"center" }}>
                  <div style={{ fontSize:"3rem", marginBottom:"1.25rem" }}>✅</div>
                  <h3 style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontWeight:800, fontSize:"1.4rem", color:"#0f172a", marginBottom:".9rem" }}>Message Sent!</h3>
                  <p style={{ color:"#475569", lineHeight:1.7 }}>Our team at <strong style={{ color:"#96611b" }}>ravi.pandey@peepalresearch.com</strong> will respond within one business day.</p>
                </div>
              ) : (
                <form onSubmit={submitContact} style={{ display:"flex", flexDirection:"column", gap:"1.05rem" }}>
                  <div style={{ display:"grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap:"1.05rem" }}>
                    <div><label>Full Name *</label><input required value={form.name} onChange={e => setForm(d => ({...d, name:e.target.value}))} placeholder="Jane Smith"/></div>
                    <div><label>Email *</label><input required type="email" value={form.email} onChange={e => setForm(d => ({...d, email:e.target.value}))} placeholder="jane@company.com"/></div>
                  </div>
                  <div style={{ display:"grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap:"1.05rem" }}>
                    <div><label>Company</label><input value={form.company} onChange={e => setForm(d => ({...d, company:e.target.value}))} placeholder="Your organization"/></div>
                    <div><label>Subject</label><input value={form.subject} onChange={e => setForm(d => ({...d, subject:e.target.value}))} placeholder="Demo Request / Talent Inquiry…"/></div>
                  </div>
                  <div><label>Message *</label><textarea required value={form.message} onChange={e => setForm(d => ({...d, message:e.target.value}))} placeholder="Tell us about your needs — product demo, integration, or talent requirements…"/></div>
                  {formSt === "error" && (
                    <div style={{ padding:".85rem 1rem", borderRadius:8, background:"rgba(239,68,68,0.1)", border:"1px solid rgba(239,68,68,0.2)", color:"#fca5a5", fontSize:".875rem" }}>
                      ⚠️ There was an error. Please email us at info@peepalresearch.com directly.
                    </div>
                  )}
                  <button className="btn-p" type="submit" disabled={formSt==="sending"} style={{ justifyContent:"center", padding:"14px", fontSize:".975rem", opacity: formSt==="sending" ? 0.7 : 1 }}>
                    {formSt==="sending" ? "Sending…" : "Send Message →"}
                  </button>
                  <p style={{ color:"#94a3b8", fontSize:".73rem", textAlign:"center" }}>Sent securely to ravi.pandey@peepalresearch.com · Responds within 1 business day</p>
                </form>
              )}
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
}
